import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Search, Bell, Settings, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Undo2, RefreshCw, X, ChevronDown, User, LogOut, LogIn, HelpCircle, Shield, Globe, SearchX, SlidersHorizontal, Plus, Check, Moon, Sun } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './styles.css';

// Import components
import Portfolio from './components/Portfolio';
import News from './components/News';
import About from './components/About';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Help from './components/Help';
import Support from './components/Support';
import Careers from './components/Careers';
import Learn from './components/Learn';
import Login from './components/Login';
import StockDetail from './components/StockDetail';
import Footer from './components/Footer';
import { NewsletterSignupBanner } from './components/NewsletterSignupSection';
import Newsletter from './components/Newsletter';
import NewsletterConfirm from './components/NewsletterConfirm';
import Watchlist from './components/Watchlist';
import Movers from './components/Movers';
import Messages from './components/Messages';
import CacheViewer from './components/CacheViewer';
import SymbolAutocomplete from './components/SymbolAutocomplete';
import CustomRulesModal from './components/CustomRulesModal';
import Profile from './components/Profile';
import AdminDashboard from './components/AdminDashboard';
import HomePage from './components/HomePage';
import BidsigLogo from './components/BidsigLogo';
import ScreenerBulkAddDialog from './components/ScreenerBulkAddDialog';
import Disclaimer from './components/Disclaimer';
import { getCachedNewsData, processNewsFlags } from './utils/newsCache';
import { renderNewsRating } from './utils/renderNewsRating';
import { getCachedPortfolioMap, setCachedPortfolioMap, getCachedWatchlistMap, setCachedWatchlistMap } from './utils/portfolioWatchlistCache';
import { authFetch, clearAuthSession } from './utils/authFetch';
import { bootstrapAuthSession, startAuthSessionRefresh } from './utils/authTokens';
import { getCachedTradeDates, getCachedLatestTradeDatesByMarket } from './utils/tradeDatesCache';
import { getLatestTradeDateForScreenerMarket, shouldApplyScreenerLiveQuotes, resolveMarketForStock } from './utils/marketTradeDates';
import { fetchStocksByTradeDate, seedStocksCacheForDate } from './utils/stocksByMarketDate';
import { applyCustomRules } from './utils/evaluateCustomRule';
import { getCachedSymbolNames, getCachedSymbolNamesSync, normalizeSymbolNameEntry } from './utils/symbolNamesCache';
import { applyLiveQuotesToStocks, startLiveMarketClientCache, clearLiveMarketClientCache } from './utils/liveMarketData';
import { useLiveMarketDisplay } from './hooks/useLiveMarketDisplay';
import {
  shouldUseFreshClientCache,
  clearClientCacheBypassFlag,
  getClientCacheReloadScope,
} from './utils/clientCacheReload';
import {
  getCachedFilterPresets,
  setCachedFilterPresets,
  hydrateFilterPresets,
  refreshFilterPresetsFromServer,
} from './utils/filterPresetsCache';
import { useTheme, toggleThemeMode, applyStoredTheme } from './utils/theme';

const SCREENER_SESSION_META_KEY = 'screenerSessionMeta';
const SCREENER_ACTIVE_FILTERS_KEY = 'screenerActiveFilters';

const CACHE_TTL_MAP = {
  'portfolioSymbolsCache_': 24 * 60 * 60 * 1000,
  'watchlistSymbolsCache_': 24 * 60 * 60 * 1000,
  'portfolioData_': 60 * 60 * 1000,
  'watchlistData_': 60 * 60 * 1000,
  'newsDataCache_': 2 * 60 * 1000,
  'tradeDatesCache': 60 * 60 * 1000,
  'latestTradeDateCache': 60 * 60 * 1000,
  'latestTradeDateByMarketCache': 30 * 60 * 1000,
  'symbolNamesCache': 60 * 60 * 1000,
  // liveMarketQuotesCache / liveMarketExtendedQuotesCache manage their own
  // freshness (regular quotes: 180s; extended: persist until next update).
  'filterPresets_': 7 * 24 * 60 * 60 * 1000,
};

const purgeLegacyBulkClientCaches = () => {
  /* cachedStockData is actively used for screener + PW coalescing — do not purge on startup */
};

const cleanupExpiredCaches = () => {
  const now = Date.now();
  let removed = 0;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (!key) continue;
    for (const [prefix, ttl] of Object.entries(CACHE_TTL_MAP)) {
      if (key === prefix || key.startsWith(prefix)) {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) break;
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.timestamp === 'number' && (now - parsed.timestamp) >= ttl) {
            localStorage.removeItem(key);
            removed++;
          }
        } catch {
          localStorage.removeItem(key);
          removed++;
        }
        break;
      }
    }
  }
  if (removed > 0) {
    console.log(`[CACHE CLEANUP] Removed ${removed} expired cache entries on startup`);
  }
};

// Filter field keys, display labels, and help (mapping from Chk.js – not included in build)
const FILTER_LABELS = {
  pe: 'P/E Ratio',
  rsi: 'RSI (14)',
  close: 'Price',
  volume: 'Volume',
  market_cap: 'Market Cap (M)',
  streak: 'Current Streak (Days)',
  streak_5days: 'Up Days (5D)',
  streak_10days: 'Up Days (10D)',
  streak_20days: 'Up Days (20D)',
  pct_change: '% Change (1D)',
  sma20_prc_diff_pct: 'Price vs MA(20) Diff (%)',
  sma20_vol_diff_pct: 'Volume vs MA(20) Diff (%)',
  high_streak: 'Days Since 52W High',
  high_prc_52weeks_pct_diff: 'Price vs 52W High Diff (%)',
  take_profit: 'Strategy Return (%)',
  news_rating: 'News Sentiment (0–10)',
  last_action: 'Last Signal',
  instrument: 'Asset Type',
  sector: 'Sector',
};
const FILTER_HELP = {
  pe: 'Price-to-earnings ratio. Enter a min/max range.',
  rsi: 'Relative Strength Index (0–100). Enter min/max. Common: RSI < 30 oversold, > 70 overbought.',
  close: 'Closing price in dollars. Enter min/max (e.g. 10 to 500).',
  volume: 'Trading volume. Enter min/max range.',
  market_cap: 'Market capitalization in millions (M). Enter min/max range.',
  streak: 'Current consecutive up/down streak in days. Enter min/max range.',
  streak_5days: 'Number of up days in the last 5 sessions. Enter min/max (0–5).',
  streak_10days: 'Number of up days in the last 10 sessions. Enter min/max (0–10).',
  streak_20days: 'Number of up days in the last 20 sessions. Enter min/max (0–20).',
  pct_change: '1-day percent change from previous close. Enter min/max (e.g. -10 to 20).',
  sma20_prc_diff_pct: 'Percent difference between price and the 20-day moving average. Enter min/max.',
  sma20_vol_diff_pct: 'Percent difference between volume and its 20-day average. Enter min/max.',
  high_streak: 'Days since the last 52-week high (1 = at the high). Enter min/max range.',
  high_prc_52weeks_pct_diff: 'Percent below the 52-week high. Enter min/max range.',
  take_profit: 'Strategy return (take-profit %) for the selected strategy. Enter min/max (e.g. 50 to 100).',
  news_rating: 'News sentiment score (0–10). Enter min/max to filter by latest rating.',
  last_action: 'Last signal: Buy, Sell, or All to show every symbol.',
  instrument: 'Asset type: Stock, ETF, or Fund. Select one or All.',
  sector: 'Industry sector (e.g. Technology, Healthcare). Select one or All.',
};
const FILTER_KEYS = Object.keys(FILTER_LABELS);
// Filter dialog fields only — instrument is chosen via screener tabs, not the modal
const FILTER_MODAL_KEYS = FILTER_KEYS.filter((k) => k !== 'instrument');
// Match server STOCKS_CACHE_TTL for background refresh of stock data when viewing latest date (1 hour)
const STOCKS_DISPLAY_REFRESH_MS = 30 * 60 * 1000;

/** Lowercase alphanumerics only — matches `index` values that use spaces, punctuation, or `|` (e.g. S&P 500 ↔ sp500). */
const indexFilterSlug = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const PATTERN_FILTER_LABELS = {
  cup_handle: 'Cup & Handle',
  double_top: 'Double Top',
  double_bottom: 'Double Bottom',
};

/** Toolbar / tab defaults on first load (modal filters are separate). */
const SCREENER_VIEW_DEFAULTS = {
  market: 'US',
  indexFilter: 'all',
  patternFilter: '',
  instrument: 'Stock',
};

/** Market-cap size buckets (raw market_cap in dollars). None selected = show all. */
const MARKET_CAP_BUCKETS = [
  { key: 'micro', label: 'Micro-cap', title: 'Micro-cap: 50 million to 300 million', min: 50e6, max: 300e6 },
  { key: 'small', label: 'Small-cap', title: 'Small-cap: 300 million to 2 billion', min: 300e6, max: 2e9 },
  { key: 'mid', label: 'Mid-cap', title: 'Mid-cap: 2 billion to 10 billion', min: 2e9, max: 10e9 },
  { key: 'large', label: 'Large-cap', title: 'Large-cap: 10 billion to 200 billion', min: 10e9, max: 200e9 },
  { key: 'mega', label: 'Mega-cap', title: 'Mega-cap: 200 billion and greater', min: 200e9, max: null },
];

const stockMatchesMarketCapBucket = (marketCap, bucket) => {
  const cap = parseFloat(marketCap);
  if (isNaN(cap)) return false;
  if (bucket.max == null) return cap >= bucket.min;
  return cap >= bucket.min && cap <= bucket.max;
};

const formatScreenerRange = (range) => {
  if (!range) return null;
  const min = range.min !== undefined && range.min !== '' ? range.min : null;
  const max = range.max !== undefined && range.max !== '' ? range.max : null;
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min} – ${max}`;
  if (min != null) return `≥ ${min}`;
  return `≤ ${max}`;
};

const getScreenerIndexFilterOptions = (marketKey) => {
  const usOptions = [
    { value: '', label: 'All Symbols' },
    { value: 'all', label: 'All Indices' },
    { value: 'sp500', label: 'S&P 500' },
    { value: 'nasdaq100', label: 'Nasdaq 100' },
    { value: 'dowjones', label: 'Dow Jones' },
  ];
  const tsxOption = { value: 'spTSX', label: 'S&P|TSX Comp.' };
  if (marketKey === 'CA') {
    return [{ value: '', label: 'All Symbols' }, tsxOption];
  }
  if (marketKey === '') {
    return [...usOptions, tsxOption];
  }
  return usOptions;
};


// FilterModal component
const filterModalStateFromParent = (parentFilters) =>
  FILTER_MODAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: (parentFilters || {})[k] ?? '' }), {});

const filterModalMinMaxFromComparisons = (comparisons) =>
  FILTER_MODAL_KEYS.reduce((acc, k) => {
    const range = comparisons?.[k];
    if (range && (range.min !== undefined || range.max !== undefined)) acc[k] = range;
    return acc;
  }, {});

const readPersistedScreenerFilters = () => {
  if (shouldUseFreshClientCache() || getClientCacheReloadScope() === 'global') return null;
  try {
    const raw = sessionStorage.getItem(SCREENER_ACTIVE_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const defaultScreenerFilters = () =>
  FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: k === 'instrument' ? 'Stock' : '' }), {});

const restoreScreenerFiltersState = () => {
  const persisted = readPersistedScreenerFilters();
  if (!persisted?.currentFilters) {
    return { currentFilters: defaultScreenerFilters(), comparisons: {} };
  }
  const currentFilters = FILTER_KEYS.reduce((acc, k) => ({
    ...acc,
    [k]: persisted.currentFilters[k] ?? (k === 'instrument' ? 'Stock' : ''),
  }), {});
  const comparisons =
    persisted.comparisons && typeof persisted.comparisons === 'object' ? persisted.comparisons : {};
  return { currentFilters, comparisons };
};

const filterModalStateFromDefaultPresets = (defaultPresets) => {
  const normFilters = FILTER_MODAL_KEYS.reduce(
    (acc, k) => ({ ...acc, [k]: (defaultPresets?.filters || {})[k] ?? '' }),
    {}
  );
  const normMinMax = FILTER_MODAL_KEYS.reduce((acc, k) => {
    const range = (defaultPresets?.minMaxFilters || {})[k];
    if (range && (range.min !== undefined || range.max !== undefined)) acc[k] = range;
    return acc;
  }, {});
  return { normFilters, normMinMax };
};

const FilterModal = ({ isOpen, onClose, onApply, currentFilters, comparisons, user }) => {
  const [filters, setFilters] = useState(() => filterModalStateFromParent(currentFilters));
  const [minMaxFilters, setMinMaxFilters] = useState(() => filterModalMinMaxFromComparisons(comparisons));
  const [initialFilters, setInitialFilters] = useState(() => filterModalStateFromParent(currentFilters));
  const [initialMinMaxFilters, setInitialMinMaxFilters] = useState(() => filterModalMinMaxFromComparisons(comparisons));
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [presetsVersion, setPresetsVersion] = useState(0);
  const [savedPresets, setSavedPresets] = useState(null);
  const modalRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  const loadDefaultPresets = () => {
    if (!user?.email) return null;
    return savedPresets || getCachedFilterPresets(user.email);
  };

  // Save default presets to DynamoDB and refresh local read cache
  const saveDefaultPresetsToDynamoDB = async (filtersToSave, minMaxFiltersToSave) => {
    if (!user || !user.email) return false;
    
    try {
      const response = await authFetch(`/api/filter-presets/${user.email}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filters: filtersToSave,
          minMaxFilters: minMaxFiltersToSave
        }),
      });
      
      if (response.ok) {
        setCachedFilterPresets(user.email, {
          filters: filtersToSave,
          minMaxFilters: minMaxFiltersToSave,
          timestamp: Date.now(),
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving default presets to DynamoDB:', error);
      return false;
    }
  };

  // Check if filters have changed from initial/default
  const hasFiltersChanged = () => {
    const defaultPresets = loadDefaultPresets();
    const compareFilters = defaultPresets ? defaultPresets.filters : initialFilters;
    const compareMinMax = defaultPresets ? defaultPresets.minMaxFilters : initialMinMaxFilters;
    
    // Compare filters - check all keys in both objects
    const filtersChanged = FILTER_MODAL_KEYS.some((key) => {
      const currentValue = filters[key] || '';
      const compareValue = compareFilters[key] || '';
      return currentValue !== compareValue;
    });
    
    const minMaxChanged = FILTER_MODAL_KEYS.some((key) => {
      const current = minMaxFilters[key] || {};
      const compare = compareMinMax[key] || {};
      return (current.min || '') !== (compare.min || '') ||
        (current.max || '') !== (compare.max || '');
    });
    
    return filtersChanged || minMaxChanged;
  };

  // Apply default presets (modal fields only; instrument stays on screener tabs)
  const handleApplyDefault = async () => {
    let defaultPresets = loadDefaultPresets();
    if (!defaultPresets && user?.email) {
      defaultPresets = await hydrateFilterPresets(user.email);
      if (defaultPresets) {
        setSavedPresets(defaultPresets);
        setPresetsVersion((v) => v + 1);
      }
    }
    if (defaultPresets) {
      const { normFilters, normMinMax } = filterModalStateFromDefaultPresets(defaultPresets);
      setFilters(normFilters);
      setMinMaxFilters(normMinMax);
    }
  };

  // Handle save with confirmation
  const handleSave = () => {
    setShowConfirmDialog(true);
  };

  // Confirm save action
  const confirmSave = async () => {
    const success = await saveDefaultPresetsToDynamoDB(filters, minMaxFilters);
    if (success) {
      const saved = {
        filters,
        minMaxFilters,
        timestamp: Date.now(),
      };
      setSavedPresets(saved);
      setInitialFilters(filters);
      setInitialMinMaxFilters(minMaxFilters);
      setPresetsVersion((v) => v + 1);
      handleApply();
    }
    setShowConfirmDialog(false);
  };

  // Cancel save action
  const cancelSave = () => {
    setShowConfirmDialog(false);
  };

  const sectorOptions = [
    'All',
    'Healthcare',
    'Basic Materials',
    'Financial',
    'Consumer Defensive',
    'Industrials',
    'Technology',
    'Consumer Cyclical',
    'Real Estate',
    'Communication Services',
    'Energy',
    'Utilities'
  ];

  const lastactOptions = [
    'All',
    'Buy',
    'Sell'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleRangeChange = (name, minOrMax, value) => {
    setMinMaxFilters(prev => ({
      ...prev,
      [name]: {
        ...prev[name],
        [minOrMax]: value
      }
    }));
  };

  const handleApply = () => {
    const processedFilters = FILTER_MODAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: filters[k] ?? '' }), {});
    const instrument =
      currentFilters && Object.prototype.hasOwnProperty.call(currentFilters, 'instrument')
        ? currentFilters.instrument
        : 'Stock';
    const mergedFilters = { ...processedFilters, instrument };
    const normMinMax = FILTER_MODAL_KEYS.reduce((acc, k) => {
      const range = minMaxFilters[k];
      if (range && (range.min !== undefined || range.max !== undefined)) acc[k] = range;
      return acc;
    }, {});
    onApply(mergedFilters, normMinMax);
    onClose();
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Only initialize when modal first opens — show active screener filters, not saved defaults.
      if (!prevIsOpenRef.current) {
        const normFilters = filterModalStateFromParent(currentFilters);
        const normMinMax = filterModalMinMaxFromComparisons(comparisons);
        setFilters(normFilters);
        setMinMaxFilters(normMinMax);
        setInitialFilters(normFilters);
        setInitialMinMaxFilters(normMinMax);

        if (user?.email) {
          hydrateFilterPresets(user.email).then((presets) => {
            setSavedPresets(presets);
            setPresetsVersion((v) => v + 1);
          });
        }
      }
      prevIsOpenRef.current = true;
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      setShowConfirmDialog(false);
      setSavedPresets(null);
      prevIsOpenRef.current = false;
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, user?.email, currentFilters, comparisons]);

  const resetFilters = () => {
    const emptyFilters = FILTER_MODAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {});
    setFilters(emptyFilters);
    setMinMaxFilters({});
    setInitialFilters(emptyFilters);
    setInitialMinMaxFilters({});
  };

  const defaultPresetsAvailable = Boolean(
    user?.email && presetsVersion >= 0 && (savedPresets || getCachedFilterPresets(user.email))
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal screener-filter-modal" ref={modalRef}>
        <div className="modal-content">
          <div className="modal-header screener-filter-modal-header">
            <div className="screener-filter-modal-heading">
              <h3>Advanced Filters</h3>
              <p className="screener-filter-modal-sub">Set min/max ranges or pick options, then apply.</p>
            </div>
            <div className="modal-header-buttons">
              <button onClick={resetFilters} className="reset-button" title="Reset Filters">
                <Undo2 />
              </button>
              <button onClick={onClose} className="modal-close" aria-label="Close filters">
                <X />
              </button>
            </div>
          </div>
          <div className="modal-body screener-filter-body">
            {FILTER_MODAL_KEYS.map((key) => {
              const value = filters[key] ?? '';
              const isNumericField = !['sector', 'last_action'].includes(key);
              const label = FILTER_LABELS[key] || key;
              const helpText = FILTER_HELP[key];
              
              if (isNumericField) {
                return (
                  <div key={key} className="form-group">
                    <label htmlFor={key} className="filter-label-with-help">
                      <span>{label}</span>
                      {helpText && (
                        <span className="filter-help-icon" title={helpText} aria-label="Help">
                          <HelpCircle size={13} />
                        </span>
                      )}
                    </label>
                    <div className="filter-range-inputs">
                      <input
                        type="number"
                        placeholder="Min"
                        value={minMaxFilters[key]?.min || ''}
                        onChange={(e) => handleRangeChange(key, 'min', e.target.value)}
                        className="range-input"
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={minMaxFilters[key]?.max || ''}
                        onChange={(e) => handleRangeChange(key, 'max', e.target.value)}
                        className="range-input"
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div key={key} className="form-group">
                  <label htmlFor={key} className="filter-label-with-help">
                    <span>{label}</span>
                    {helpText && (
                      <span className="filter-help-icon" title={helpText} aria-label="Help">
                        <HelpCircle size={13} />
                      </span>
                    )}
                  </label>
                  <div className="filter-input-group">
                    {key === 'sector' ? (
                      <select
                        id={key}
                        name={key}
                        value={value}
                        onChange={(e) => handleChange(e)}
                        className="sector-select"
                      >
                        {sectorOptions.map(option => (
                          <option key={option} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : key === 'last_action' ? (
                      <select
                        id={key}
                        name={key}
                        value={value}
                        onChange={(e) => handleChange(e)}
                        className="action-select"
                      >
                        {lastactOptions.map(option => (
                          <option key={option} value={option === 'All' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="modal-footer-buttons">
            {defaultPresetsAvailable ? (
              <button 
                onClick={handleApplyDefault} 
                className="default-button"
                title="Apply Default Presets"
              >
                Default
              </button>
            ) : (
              <button 
                className="default-button"
                disabled
                title="No default presets saved"
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                Default
              </button>
            )}
            <button 
              onClick={handleSave} 
              className="save-button"
              disabled={!hasFiltersChanged() || !user}
              title={!user ? "Please log in to save presets" : hasFiltersChanged() ? "Save as Default Presets" : "No changes to save"}
              style={(!hasFiltersChanged() || !user) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              onMouseDown={(e) => {
                if (!hasFiltersChanged() || !user) {
                  e.preventDefault();
                }
              }}
            >
              Save
            </button>
            <button onClick={handleApply} className="apply-button">
              Apply Filters
            </button>
          </div>
          {showConfirmDialog && (
            <div className="confirm-dialog-overlay">
              <div className="confirm-dialog">
                <h4>Save Default Presets?</h4>
                <p>This will save the current filter settings as your default presets. Use the Default button to load them later.</p>
                <div className="confirm-dialog-buttons">
                  <button onClick={confirmSave} className="confirm-button">
                    Save & Apply
                  </button>
                  <button onClick={cancelSave} className="cancel-button">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Resolve symbol_id key in symbolName map (case-insensitive). */
function resolveSymbolMapKey(raw, symbolMap) {
  if (!raw || !symbolMap) return null;
  const t = raw.trim();
  if (symbolMap[t]) return t;
  return Object.keys(symbolMap).find((k) => k.toUpperCase() === t.toUpperCase()) || null;
}

const DEFAULT_SORT_FIELD = 'symbol';
const DEFAULT_SORT_ORDER = 'asc';

const SCREENER_SORT_COLUMNS = [
  { key: 'symbol', label: 'Symbol', title: 'Stock symbol', type: 'string' },
  { key: 'name', label: 'Company', title: 'Company name', type: 'string' },
  { key: 'sector', label: 'Sector', title: 'Sector', type: 'string' },
  { key: 'market_cap', label: 'Market Cap', title: 'Market capitilization', type: 'number' },
  { key: 'close', label: 'Price', title: 'Stock price', type: 'number' },
  { key: 'pct_change', label: '% Change', title: '% Change from previous close', type: 'number' },
  { key: 'trade_timestamp', label: 'Updated At', title: 'Trade timestamp', type: 'date' },
  { key: 'price_ext', label: 'Price Ext.', title: 'Extended hours price (pre-market / after-hours)', type: 'number' },
  { key: 'pct_change_ext', label: '% Change Ext', title: 'Extended hours % change vs last regular close', type: 'number' },
  { key: 'currency', label: 'Currency', title: 'Quote currency', type: 'string' },
  { key: 'volume', label: 'Volume', title: '1-day Volume traded', type: 'number' },
  { key: 'rel_volume', label: 'Rel Volume', title: 'Relative Volume - 1 Day vs. 20 Day Avg)', type: 'number' },
  { key: 'news', label: 'News', title: 'Latest news sentiment', type: 'number' },
  { key: 'take_profit', label: 'Returns', title: 'Take-profit return (%) for selected strategy', type: 'number' },
  { key: 'first_buy', label: 'First Bought', title: 'First buy date for buy/hold or selected strategy', type: 'date' },
  // { key: 'hold_profit', label: 'Performance', title: 'Performance (%) since first buy for buy/hold strategy', type: 'number' },
  { key: 'signal', label: 'Signal', title: 'Technical signal', type: 'string' },
  { key: 'short_term_sentiment', label: 'AI Sentiment', title: 'AI sentiment short-term outlook rating', type: 'string' },
  { key: 'beta', label: 'Beta', title: 'Volatility compared to market', type: 'number' },
  { key: 'float_shares', label: 'Float', title: 'Float Shares', type: 'number' },
  { key: 'atr', label: 'ATR', title: 'Average True Range', type: 'number' },
  { key: 'pe', label: 'PE', title: 'Price-to-Earnings ratio', type: 'number' },
  { key: 'rsi', label: 'RSI', title: 'Relative Strength Index', type: 'number' },
  { key: 'sma20_prc', label: 'MA20D', title: '20-day Moving Average', type: 'number' },
  { key: 'sma50_prc', label: 'MA50D', title: '50-day Moving Average', type: 'number' },
  { key: 'sma200_prc', label: 'MA200D', title: '200-day Moving Average', type: 'number' },
];

function compareSortValues(a, b, type, order) {
  const aEmpty = a == null || a === '' || a === '--';
  const bEmpty = b == null || b === '' || b === '--';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp = 0;
  if (type === 'number') {
    cmp = Number(a) - Number(b);
    if (!Number.isFinite(cmp)) cmp = 0;
  } else if (type === 'date') {
    cmp = String(a).localeCompare(String(b));
  } else {
    cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  }
  return order === 'desc' ? -cmp : cmp;
}

// StockList component
const StockList = ({
  data,
  type,
  user,
  signalField = 'status',
  takeProfitField = 'take_profit',
  holdProfitField = 'hold_profit1',
  firstBuyDateField = 'first_buy_date1',
  useCustomRules = false,
}) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_ORDER);
  const [newsFlags, setNewsFlags] = useState({}); // { symbol_id: ... }
  const [newsFlagRatings, setNewsFlagRatings] = useState({});
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const selectAllRef = useRef(null);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const getSignalStatus = useCallback((item) => item.new_status || item[signalField], [signalField]);

  const getSortValue = useCallback((item, fieldKey) => {
    switch (fieldKey) {
      case 'symbol': return item.symbol;
      case 'name': return item.name;
      case 'sector': return item.sector;
      case 'market_cap': return item.market_cap;
      case 'close': return item.close;
      case 'currency': return item.currency;
      case 'volume': return item.volume;
      case 'rel_volume':
        return item.average_volume != null ? item.volume / item.average_volume : null;
      case 'trade_date': return item.trade_date;
      case 'pct_change': return item.pct_change;
      case 'news': return newsFlagRatings[item.symbol_id] ?? null;
      case 'take_profit': return item[takeProfitField];
      case 'first_buy': return item[firstBuyDateField];
      case 'hold_profit': return item[holdProfitField];
      case 'signal': return getSignalStatus(item);
      case 'short_term_sentiment': return item.short_term_sentiment;
      case 'beta': return item.beta;
      case 'float_shares': return item.float_shares;
      case 'atr': return item.atr;
      case 'pe': return item.pe;
      case 'rsi': return item.rsi;
      case 'sma20_prc': return item.sma20_prc;
      case 'sma50_prc': return item.sma50_prc;
      case 'sma200_prc': return item.sma200_prc;
      default: return item[fieldKey];
    }
  }, [newsFlagRatings, takeProfitField, holdProfitField, firstBuyDateField, getSignalStatus]);

  const handleColumnSort = (fieldKey) => {
    setPage(1);
    if (sortField === fieldKey) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') setSortDirection('neutral');
      else setSortDirection('asc');
    } else {
      setSortField(fieldKey);
      setSortDirection('asc');
    }
  };

  // Reset page to 1 when data changes (e.g., when filters/search are applied)
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [data.length]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [page]);

  // Ensure page doesn't exceed totalPages when data is filtered
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [page, totalPages]);

  const sortedData = useMemo(() => {
    const effectiveField = sortDirection === 'neutral' ? DEFAULT_SORT_FIELD : sortField;
    const effectiveOrder = sortDirection === 'neutral' ? DEFAULT_SORT_ORDER : sortDirection;
    const columnMeta = SCREENER_SORT_COLUMNS.find((c) => c.key === effectiveField);
    const sortType = columnMeta?.type || 'string';

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, effectiveField);
      const bValue = getSortValue(b, effectiveField);
      const primary = compareSortValues(aValue, bValue, sortType, effectiveOrder);
      if (primary !== 0) return primary;
      return compareSortValues(getSortValue(a, DEFAULT_SORT_FIELD), getSortValue(b, DEFAULT_SORT_FIELD), 'string', DEFAULT_SORT_ORDER);
    });
  }, [data, sortField, sortDirection, getSortValue]);

  const paginatedData = useMemo(() => {
    return sortedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [sortedData, page, itemsPerPage]);

  const pageSymbolIds = useMemo(
    () => paginatedData.map((item) => item.symbol_id).filter(Boolean),
    [paginatedData],
  );

  const selectedCount = selectedIds.size;
  const allPageSelected = pageSymbolIds.length > 0 && pageSymbolIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageSymbolIds.some((id) => selectedIds.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const toggleSelect = (symbolId) => {
    if (!symbolId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(symbolId)) next.delete(symbolId);
      else next.add(symbolId);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageSymbolIds.forEach((id) => next.delete(id));
      } else {
        pageSymbolIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const selectedStocks = useMemo(
    () => paginatedData.filter((item) => item.symbol_id && selectedIds.has(item.symbol_id)),
    [paginatedData, selectedIds],
  );

  const handleBulkAddClick = () => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    setShowBulkAdd(true);
  };

  // Fetch news flags for stock list
  const fetchNewsFlags = useCallback(async (symbolIds) => {
    if (!symbolIds || symbolIds.length === 0) {
      setNewsFlags({});
      setNewsFlagRatings({});
      return;
    }
    
    try {
      const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York'});
      const newsData = await getCachedNewsData(currentDate, true);
      const { flags, ratings } = processNewsFlags(newsData, symbolIds);
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    } catch (error) {
      console.error('Error fetching news flags:', error);
      const flags = {};
      const ratings = {};
      symbolIds.forEach((id) => {
        flags[id] = 'grey';
        ratings[id] = null;
      });
      setNewsFlags(flags);
      setNewsFlagRatings(ratings);
    }
  }, []);

  useEffect(() => {
    if (data && data.length > 0) {
      const symbolIds = data
        .map((stock) => stock?.symbol_id)
        .filter((id) => id && typeof id === 'string');

      if (symbolIds.length > 0) {
        fetchNewsFlags(symbolIds);
      } else {
        setNewsFlags({});
        setNewsFlagRatings({});
      }
    } else {
      setNewsFlags({});
      setNewsFlagRatings({});
    }
  }, [data, fetchNewsFlags]);

  const getNewsFlagTooltip = (flagColor) => {
    switch (flagColor) {
      case 'red':
        return 'Related news found with sentiment rating lte 4/10';
      case 'green':
        return 'Related news found with sentiment rating gte 7/10';
      case 'amber':
        return 'Related news found with sentiment rating eq 5/10|6/10';
      case 'grey':
      default:
        return 'No related news in cached latest news';
    }
  };

  const formatFixed = (n, digits = 2) => {
    if (n == null) return '--';
    const num = Number(n);
    return Number.isFinite(num) ? num.toFixed(digits) : '--';
  };

  const formatInt = (marketCap) => {
    if (marketCap == null) return '--';
    if (marketCap >= 1000000000000) {
      return `${(marketCap / 1000000000000).toFixed(2)}T`;
    } else if (marketCap >= 1000000000) {
      return `${(marketCap / 1000000000).toFixed(2)}B`;
    } else if (marketCap >= 1000000) {
      return `${(marketCap / 1000000).toFixed(2)}M`;
    } else if (marketCap >= 1000) {
      return `${(marketCap / 1000).toFixed(2)}K`;
    } else {
      return `${marketCap.toFixed(2)}`;
    }
  };

  const getListTitle = () => {
    switch (type) {
      case 'buy': return 'Buy Signal';
      case 'sell': return 'Sell Signal';
      case 'hold': return 'Hold Signal';
      case 'portfolio': return 'Your Portfolio';
      default: return 'Stock List';
    }
  };

  const getSignalClass = (item) => {
    const status = (getSignalStatus(item) || '').toLowerCase();
    if (status === 'buy') return 'pw-status-buy';
    if (status === 'sell') return 'pw-status-sell';
    return 'pw-status-hold';
  };

  const getSentimentClass = (sentiment) => {
    const s = (sentiment || '').toLowerCase().trim();
    if (s === 'bullish') return 'pw-status-buy';
    if (s === 'bearish') return 'pw-status-sell';
    return 'pw-status-hold';
  };

  const renderSortableHeader = (col) => {
    const isActive = sortDirection !== 'neutral' && sortField === col.key;
    const ariaSort = isActive
      ? (sortDirection === 'asc' ? 'ascending' : 'descending')
      : 'none';

    return (
      <th
        key={col.key}
        title={col.title}
        aria-sort={ariaSort}
        className={
          col.key === 'symbol' ? 'pw-col-symbol' : col.key === 'name' ? 'pw-col-company' : undefined
        }
      >
        <span className="screener-sort-header-inner">
          <span className="screener-sort-label">{col.label}</span>
          <button
            type="button"
            className="screener-sort-trigger"
            onClick={() => handleColumnSort(col.key)}
            aria-label={`Sort by ${col.label}${isActive ? `, ${sortDirection}` : ''}`}
          >
            <span
              className={`screener-sort-arrow screener-sort-arrow--up ${
                isActive && sortDirection === 'asc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'
              }`}
              aria-hidden="true"
            />
            <span
              className={`screener-sort-arrow screener-sort-arrow--down ${
                isActive && sortDirection === 'desc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'
              }`}
              aria-hidden="true"
            />
          </button>
        </span>
      </th>
    );
  };

  return (
    <div className={`pw-data-card stock-list screener-list-card screener-list-card--${type}`}>
      <div className="pw-data-header">
        <div className="pw-data-header-left">
          <span className={`screener-list-dot screener-list-dot--${type}`} aria-hidden="true" />
          <h3>{getListTitle()}</h3>
          <span className="pw-data-count">{data.length.toLocaleString()} {data.length === 1 ? 'stock' : 'stocks'}</span>
        </div>
        {selectedCount > 0 && (
          <div className="pw-data-header-right screener-bulk-actions">
            <span className="pw-data-count screener-selected-count">
              {selectedCount} selected
            </span>
            <button
              type="button"
              className="pw-btn pw-btn-primary pw-btn-sm screener-bulk-add-btn"
              onClick={handleBulkAddClick}
            >
              <Plus size={14} aria-hidden="true" />
              Add
            </button>
          </div>
        )}
      </div>
      <div className="pw-table-wrap">
        <table className="pw-table screener-sort-table pw-table--sticky-symbol">
          <thead>
            <tr>
              <th className="screener-select-col" aria-label="Select rows">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="pw-checkbox screener-select-all"
                  checked={allPageSelected}
                  onChange={toggleSelectAllPage}
                  aria-label="Select all symbols on this page"
                />
              </th>
              {SCREENER_SORT_COLUMNS.map(renderSortableHeader)}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={item.symbol_id || index} className={selectedIds.has(item.symbol_id) ? 'screener-row-selected' : undefined}>
                <td className="screener-select-col">
                  <input
                    type="checkbox"
                    className="pw-checkbox screener-row-select"
                    checked={Boolean(item.symbol_id && selectedIds.has(item.symbol_id))}
                    onChange={() => toggleSelect(item.symbol_id)}
                    aria-label={`Select ${item.symbol || item.symbol_id}`}
                  />
                </td>
                <td className="pw-col-symbol">
                  <a href={`/stock/${encodeURIComponent(String(item.symbol_id).toUpperCase())}`} target="_blank" rel="noopener noreferrer" className="pw-symbol-link">
                    {item.symbol}
                  </a>
                </td>
                <td className="pw-company-name pw-col-company">{item.name}</td>
                <td className="pw-company-name">{item.sector}</td>
                <td>{formatInt(item.market_cap)}</td>
                <td>{item.close != null ? item.close.toFixed(2) : 'N/A'}</td>
                <td>
                  <span className={`pw-change ${item.pct_change > 0 ? 'positive' : item.pct_change < 0 ? 'negative' : ''}`}>
                    {item.pct_change != null ? (item.pct_change > 0 ? '+' : '') + item.pct_change.toFixed(2) + '%' : '--'}
                  </span>
                </td>
                <td>{item.trade_timestamp}</td>
                <td>{item.price_ext != null ? `${Number(item.price_ext).toFixed(2)}` : '--'}</td>
                <td>
                  <span className={`pw-change ${item.pct_change_ext > 0 ? 'positive' : item.pct_change_ext < 0 ? 'negative' : ''}`}>
                    {item.pct_change_ext != null ? (item.pct_change_ext > 0 ? '+' : '') + Number(item.pct_change_ext).toFixed(2) + '%' : '--'}
                  </span>
                </td>
                <td>{item.currency}</td>
                <td>{item.volume}</td>
                <td>{formatFixed(item.average_volume != null ? item.volume / item.average_volume : null)}</td>
                <td>
                  <div className="news-flag-container">
                    <span 
                      className={`news-flag news-flag-${newsFlags[item.symbol_id] || 'grey'}`}
                      title={getNewsFlagTooltip(newsFlags[item.symbol_id] || 'grey')}
                    >
                      ●
                    </span>
                    {newsFlagRatings[item.symbol_id] != null && (
                      <span className="news-rating-box" title="Latest news AI rating">
                        {renderNewsRating(newsFlagRatings[item.symbol_id])}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  {useCustomRules ? (
                    '--'
                  ) : (
                    <span className={`pw-change ${item[takeProfitField] > 0 ? 'positive' : item[takeProfitField] < 0 ? 'negative' : ''}`}>
                      {item[takeProfitField] != null ? (item[takeProfitField] > 0 ? '+' : '') + item[takeProfitField].toFixed(2) + '%' : '--'}
                    </span>
                  )}
                </td>
                <td>{useCustomRules ? '--' : (item[firstBuyDateField] ? item[firstBuyDateField] : '--')}</td>
                {/* <td>
                  <span className={`pw-change ${item[holdProfitField] > 0 ? 'positive' : item[holdProfitField] < 0 ? 'negative' : ''}`}>
                    {item[holdProfitField] != null ? (item[holdProfitField] > 0 ? '+' : '') + item[holdProfitField].toFixed(2) + '%' : '--'}
                  </span>
                </td> */}
                <td>
                  <span className={`pw-status-badge ${getSignalClass(item)}`}>
                    {getSignalStatus(item)}
                  </span>
                </td>
                <td>
                  {item.short_term_sentiment ? (
                    <span className={`pw-status-badge ${getSentimentClass(item.short_term_sentiment)}`}>
                      {item.short_term_sentiment}
                    </span>
                  ) : (
                    ''
                  )}
                </td>
                <td>{item.beta}</td>
                <td>{formatInt(item.float_shares)}</td>
                <td>{item.atr}</td>
                <td>{formatFixed(item.pe)}</td>
                <td>{formatFixed(item.rsi)}</td>
                <td>{formatFixed(item.sma20_prc)}</td>
                <td>{formatFixed(item.sma50_prc)}</td>
                <td>{formatFixed(item.sma200_prc)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showBulkAdd && selectedStocks.length > 0 && (
        <ScreenerBulkAddDialog
          stocks={selectedStocks}
          onClose={() => setShowBulkAdd(false)}
          onSuccess={() => {
            setSelectedIds(new Set());
            setShowBulkAdd(false);
          }}
        />
      )}
      <div className="pw-pagination">
        <button onClick={() => setPage(1)} disabled={page === 1} title="First Page">
          <ChevronsLeft size={18} />
        </button>
        <button onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page === 1} title="Previous Page">
          <ChevronLeft size={18} />
        </button>
        <span className="pw-page-info">Page {page} of {totalPages}</span>
        <button onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} disabled={page === totalPages} title="Next Page">
          <ChevronRight size={18} />
        </button>
        <button onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last Page">
          <ChevronsRight size={18} />
        </button>
      </div>
    </div>
  );
};

const Navbar = ({ user, setUser }) => {
  const [showTrackSubmenu, setShowTrackSubmenu] = useState(false);
  const [showResourcesSubmenu, setShowResourcesSubmenu] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    clearAuthSession();
    clearLiveMarketClientCache({ clearExtended: true });
    setUser(null);
    navigate('/');
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  const onLoginPage = location.pathname === '/login';

  return (
    <nav className="navbar">
      <button
        className="hamburger-toggle"
        onClick={() => setMobileMenuOpen((prev) => !prev)}
        aria-label="Toggle navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        {mobileMenuOpen ? <X size={24} /> : <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>}
      </button>
      <ul className={`nav-list ${mobileMenuOpen ? 'nav-list-open' : ''}`}>
        <li className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
          <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
        </li>
        <li className={`nav-item ${location.pathname === '/screener' ? 'active' : ''}`}>
          <Link to="/screener" onClick={() => setMobileMenuOpen(false)}>Screener</Link>
        </li>
        <li className={`nav-item ${location.pathname === '/movers' ? 'active' : ''}`}>
          <Link to="/movers" onClick={() => setMobileMenuOpen(false)}>Movers</Link>
        </li>
        <li className={`nav-item ${location.pathname === '/news' ? 'active' : ''}`}>
          <Link to="/news" onClick={() => setMobileMenuOpen(false)}>News</Link>
        </li>
        <li className={`nav-item ${location.pathname === '/messages' ? 'active' : ''}`}>
          <Link to="/messages" onClick={() => setMobileMenuOpen(false)}>
            <span>Messages</span>
          </Link>
        </li>
        <li
          className={`nav-item track ${
            ['/portfolio', '/watchlist'].includes(location.pathname) ? 'active' : ''
          }`}
          onMouseEnter={() => setShowTrackSubmenu(true)}
          onMouseLeave={() => setShowTrackSubmenu(false)}
          onClick={() => setShowTrackSubmenu((prev) => !prev)}
        >
          Track <ChevronDown size={16} />
          {showTrackSubmenu && (
            <ul className="submenu">
              <li className={location.pathname === '/portfolio' ? 'active' : ''}>
                <Link to="/portfolio" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
              </li>
              <li className={location.pathname === '/watchlist' ? 'active' : ''}>
                <Link to="/watchlist" onClick={() => setMobileMenuOpen(false)}>Watchlist</Link>
              </li>
            </ul>
          )}
        </li>
        <li 
          className={`nav-item resources ${
            ['/about', '/support', '/learn', '/help'].includes(location.pathname) ? 'active' : ''
          }`} 
          onMouseEnter={() => setShowResourcesSubmenu(true)} 
          onMouseLeave={() => setShowResourcesSubmenu(false)}
          onClick={() => setShowResourcesSubmenu((prev) => !prev)}
        >
          Resources <ChevronDown size={16} />
          {showResourcesSubmenu && (
            <ul className="submenu">
              <li className={location.pathname === '/about' ? 'active' : ''}>
                <Link to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
              </li>
              <li className={location.pathname === '/help' ? 'active' : ''}>
                <Link to="/help" onClick={() => setMobileMenuOpen(false)}>Help</Link>
              </li>
              <li className={location.pathname === '/learn' ? 'active' : ''}>
                <Link to="/learn" onClick={() => setMobileMenuOpen(false)}>Learn</Link>
              </li>
              <li className={location.pathname === '/support' ? 'active' : ''}>
                <Link to="/support" onClick={() => setMobileMenuOpen(false)}>Support</Link>
              </li>
            </ul>
          )}
        </li>
        {user ? (
          <li
            className={`nav-item user-menu-wrapper ${
              ['/profile', '/admin'].includes(location.pathname) ? 'active' : ''
            }`}
            ref={userMenuRef}
          >
            <button
              type="button"
              className="user-menu-trigger"
              onClick={() => setUserMenuOpen((prev) => !prev)}
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
            >
              <User size={16} />
              <span>{user.first_name}</span>
              <ChevronDown size={16} />
            </button>
            {userMenuOpen && (
              <ul className="user-menu-dropdown">
                <li className={location.pathname === '/profile' ? 'active' : ''}>
                  <Link to="/profile" className="user-menu-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                    <User size={16} />
                    Account
                  </Link>
                </li>
                {user.role === 'admin' && (
                  <li className={location.pathname === '/admin' ? 'active' : ''}>
                    <Link to="/admin" className="user-menu-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <Shield size={16} />
                      Admin
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    className="user-menu-dropdown-item user-menu-signout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </li>
              </ul>
            )}
          </li>
        ) : (
          <li className={`nav-item nav-auth ${onLoginPage ? 'active' : ''}`}>
            <Link
              to="/login"
              className={`nav-auth-link${onLoginPage ? ' is-active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Log in or sign up"
            >
              <span>Log in</span>
              <span className="nav-auth-sep" aria-hidden="true">|</span>
              <span>Sign up</span>
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
};

const BidsigDashboard = () => {
  const [allStocksData, setAllStocksData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(() => restoreScreenerFiltersState().currentFilters);
  const [comparisons, setComparisons] = useState(() => restoreScreenerFiltersState().comparisons);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState(null);
  const [symbolMap, setSymbolMap] = useState({});
  const [portfolio, setPortfolio] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [user, setUser] = useState(null);
  const [tradeDates, setTradeDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [cachedStockData, setCachedStockData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCustomRulesModalOpen, setIsCustomRulesModalOpen] = useState(false);
  const [customRules, setCustomRules] = useState(null);
  const [useCustomRules, setUseCustomRules] = useState(false);
  const [showDashboardHelp, setShowDashboardHelp] = useState(false);
  const [strategy, setStrategy] = useState('momentum');
  const [selectedMarket, setSelectedMarket] = useState('US');
  const [latestTradeDatesByMarket, setLatestTradeDatesByMarket] = useState({ US: null, CA: null });
  const [screenerDateNotice, setScreenerDateNotice] = useState(null);
  const [screenerSymbolMarketNotice, setScreenerSymbolMarketNotice] = useState(null);
  const [activeSignalTab, setActiveSignalTab] = useState('buy');
  const [newsRatingsMap, setNewsRatingsMap] = useState({});
  const [indexFilter, setIndexFilter] = useState('all');
  const [patternFilter, setPatternFilter] = useState('');
  const [selectedMarketCaps, setSelectedMarketCaps] = useState([]);
  const dashboardHelpRef = useRef(null);

  const signalField = strategy === 'momentum' ? 'status2' : 'status1';
  const lastActionField = strategy === 'momentum' ? 'last_action2' : 'last_action1';
  const takeProfitField = strategy === 'momentum' ? 'take_profit2' : 'take_profit1';
  const holdProfitField = strategy === 'momentum' ? 'hold_profit2' : 'hold_profit1';
  const firstBuyDateField = strategy === 'momentum' ? 'first_buy_date2' : 'first_buy_date1';

  const tradeDateFromYmd = useCallback((ymd) => {
    if (!ymd) return null;
    const fromList = tradeDates.find((d) => d.toISOString().split('T')[0] === ymd);
    if (fromList) return new Date(fromList);
    const parsed = new Date(`${ymd}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [tradeDates]);

  const marketLabelForKey = (marketKey) => {
    if (marketKey === 'CA') return 'Canada';
    if (marketKey === 'US') return 'United States';
    return 'all markets';
  };

  /** Notice when active market has no rows for the selected date (incl. other market's latest date). */
  const buildScreenerDateNotice = (formattedDate, marketKey, latestByMarket, processedData) => {
    if (!formattedDate) return null;

    const usLatest = latestByMarket?.US;
    const caLatest = latestByMarket?.CA;
    const marketLatestYmd = getLatestTradeDateForScreenerMarket(latestByMarket, marketKey);
    const data = Array.isArray(processedData) ? processedData : [];
    const isAllMarkets = marketKey === '' || marketKey == null;
    const marketStocks = isAllMarkets ? data : data.filter((s) => resolveMarketForStock(s) === marketKey);
    const missingForMarket = data.length === 0 || marketStocks.length === 0;

    if (!missingForMarket) return null;

    const otherLatest = isAllMarkets ? null : (marketKey === 'US' ? caLatest : usLatest);
    const otherLabel = isAllMarkets ? null : (marketKey === 'US' ? 'Canada' : 'United States');
    const isOtherMarketLatest = Boolean(otherLatest && formattedDate === otherLatest);

    if (marketLatestYmd && formattedDate !== marketLatestYmd) {
      return {
        type: 'wrong-date',
        selectedDate: formattedDate,
        marketLatest: marketLatestYmd,
        marketKey,
        marketLabel: marketLabelForKey(marketKey),
        isOtherMarketLatest,
        otherMarketLabel: otherLabel,
        otherMarketLatest: otherLatest,
      };
    }

    return {
      type: 'no-data',
      selectedDate: formattedDate,
      marketLatest: marketLatestYmd,
      marketKey,
      marketLabel: marketLabelForKey(marketKey),
      isOtherMarketLatest,
      otherMarketLabel: otherLabel,
      otherMarketLatest: otherLatest,
    };
  };

  const fetchStocksData = useCallback(async (date, isBackgroundRefresh = false, marketKeyOverride = null, latestByMarketOverride = null) => {
    // Show loading only if no cached data and not a background refresh
    
    if (!cachedStockData && !isBackgroundRefresh) {
      setIsLoading(true);
    }
    
    // Always show refresh indicator during data fetch (unless caller manages it)
    if (!isBackgroundRefresh) {
      setIsRefreshing(true);
    }
    try {
      const formattedDate = date.toISOString().split('T')[0];
      const stockRows = await fetchStocksByTradeDate(formattedDate, {
        screenerCache:
          cachedStockData?.date === formattedDate && !useCustomRules ? cachedStockData : null,
      });

      let processedData = stockRows;

      // Apply custom rules if enabled
      if (useCustomRules && customRules) {
        processedData = applyCustomRules(stockRows, customRules);
      }

      if (!Array.isArray(processedData)) {
        console.warn('[SCREENER] applyCustomRules returned non-array, resetting to []');
        processedData = [];
      }

      if (stockRows.length > 0) {
        seedStocksCacheForDate(formattedDate, stockRows);
      }

      setAllStocksData(processedData);
      // filteredData is updated by applyFilters (useEffect) — not here. Setting the raw universe
      // on cache hits left buy/sell/hold showing all markets while selectedMarket stayed US.

      const marketKey = marketKeyOverride != null ? marketKeyOverride : selectedMarket;
      const latestByMarket = latestByMarketOverride || await getCachedLatestTradeDatesByMarket();
      if (!latestByMarketOverride) {
        setLatestTradeDatesByMarket(latestByMarket);
      }
      const notice = buildScreenerDateNotice(formattedDate, marketKey, latestByMarket, processedData);
      const isEmpty = !processedData || !Array.isArray(processedData) || processedData.length === 0;

      if (notice) {
        if (isEmpty) {
          console.log('[STOCK DATA CACHE] Empty stock data received, skipping cache write');
        } else {
          console.log(`[SCREENER] No ${marketKey} stocks on ${formattedDate} (${processedData.length} rows for other markets)`);
        }
        setScreenerDateNotice(notice);
      } else if (!isEmpty) {
        setScreenerDateNotice(null);
        const newCachedData = {
          date: formattedDate,
          data: processedData,
          timestamp: Date.now()
        };

        setCachedStockData(newCachedData);
        try {
          if (stockRows.length > 0) {
            localStorage.setItem('cachedStockData', JSON.stringify({
              date: formattedDate,
              data: stockRows,
              timestamp: Date.now(),
            }));
          }
          sessionStorage.setItem(SCREENER_SESSION_META_KEY, JSON.stringify({
            date: formattedDate,
            timestamp: Date.now(),
          }));
          localStorage.setItem('selectedDate', date.toISOString());
        } catch (storageError) {
          console.warn('Could not persist screener session metadata:', storageError.message);
        }
      }
    } catch (error) {
      console.error('Error fetching stocks data:', error);
    } finally {
      if (!isBackgroundRefresh) {
        setIsLoading(false);
      }
      // Always hide refresh indicator when done (unless caller manages it)
      if (!isBackgroundRefresh) {
        setIsRefreshing(false);
      }
    }
  }, [cachedStockData, useCustomRules, customRules, selectedMarket]);

  const applyMarketLatestTradeDate = useCallback(async (marketKey, { fetchData = true } = {}) => {
    const latestByMarket = await getCachedLatestTradeDatesByMarket();
    setLatestTradeDatesByMarket(latestByMarket);
    const ymd = getLatestTradeDateForScreenerMarket(latestByMarket, marketKey);
    if (!ymd) return null;
    const pickerDate = tradeDateFromYmd(ymd);
    if (!pickerDate) return null;
    setSelectedDate(pickerDate);
    localStorage.setItem('selectedDate', pickerDate.toISOString());
    setScreenerDateNotice(null);
    if (fetchData) {
      await fetchStocksData(pickerDate, false, marketKey, latestByMarket);
    }
    return ymd;
  }, [tradeDateFromYmd, fetchStocksData]);

  useEffect(() => {
    const loadSessionMeta = () => {
      if (shouldUseFreshClientCache() || getClientCacheReloadScope() === 'global') {
        return;
      }
      try {
        const metaRaw = sessionStorage.getItem(SCREENER_SESSION_META_KEY);
        const selectedRaw = localStorage.getItem('selectedDate');
        const dateRaw = selectedRaw || (metaRaw ? JSON.parse(metaRaw).date : null);
        if (dateRaw) {
          const restored = new Date(dateRaw.includes('T') ? dateRaw : `${dateRaw}T12:00:00`);
          if (!Number.isNaN(restored.getTime())) {
            setSelectedDate(restored);
          }
        }
      } catch (e) {
        console.warn('[SCREENER] Could not restore session metadata:', e);
      }
    };

    const initializeData = async () => {
      cleanupExpiredCaches();
      purgeLegacyBulkClientCaches();

      const storedUser = JSON.parse(localStorage.getItem('user'));
      setUser(storedUser);

      loadSessionMeta();
      await fetchTradeDates();

      const freshFetch = shouldUseFreshClientCache();
      getCachedSymbolNames(null, !freshFetch).then(symbols => {
        if (symbols && Object.keys(symbols).length > 0) {
          setSymbolMap(symbols);
        }
      }).catch(error => {
        console.warn('Error fetching symbol names:', error);
      });

      if (storedUser) {
        fetchPortfolio(storedUser.email);
        fetchWatchlist(storedUser.email);
        fetchCustomRules(storedUser.email);
        bootstrapAuthSession().then(() => {
          hydrateFilterPresets(storedUser.email).catch((error) => {
            console.warn('Error hydrating filter presets:', error);
          });
        });
      }

      if (freshFetch) {
        clearClientCacheBypassFlag();
      }

      setIsInitialLoad(false);
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (isInitialLoad) return;
    try {
      sessionStorage.setItem(SCREENER_ACTIVE_FILTERS_KEY, JSON.stringify({
        currentFilters,
        comparisons,
        timestamp: Date.now(),
      }));
    } catch (e) {
      console.warn('[SCREENER] Could not persist active filters:', e);
    }
  }, [currentFilters, comparisons, isInitialLoad]);

  useEffect(() => {
    if (!isInitialLoad && selectedDate) {
      const currentDate = selectedDate.toISOString().split('T')[0];
      const cachedDate = cachedStockData?.date;
      
      if (cachedDate !== currentDate) {
        fetchStocksData(selectedDate);
      }
    }
  }, [selectedDate, isInitialLoad, cachedStockData, fetchStocksData]);

  // Background refresh: when viewing this market's latest trade date, refetch periodically
  useEffect(() => {
    if (!tradeDates.length || !selectedDate || isInitialLoad) return;
    const selectedYmd = selectedDate.toISOString().split('T')[0];
    let intervalId = null;
    let cancelled = false;

    const runRefresh = async () => {
      if (cancelled) return;
      try {
        const latestByMarket = await getCachedLatestTradeDatesByMarket(false);
        if (cancelled) return;
        setLatestTradeDatesByMarket(latestByMarket);
        const marketKey = selectedMarketRef.current;
        const latestYmd = getLatestTradeDateForScreenerMarket(latestByMarket, marketKey);
        const currentYmd = selectedDateRef.current?.toISOString().split('T')[0];
        if (latestYmd && latestYmd !== currentYmd) {
          const pickerDate = tradeDateFromYmd(latestYmd);
          if (pickerDate) {
            setSelectedDate(pickerDate);
            localStorage.setItem('selectedDate', pickerDate.toISOString());
            setScreenerDateNotice(null);
            await fetchStocksData(pickerDate, true, marketKey, latestByMarket);
            return;
          }
        }
        if (selectedDateRef.current && latestYmd && currentYmd === latestYmd) {
          fetchStocksData(selectedDateRef.current, true);
        }
      } catch { /* ignore */ }
    };

    getCachedLatestTradeDatesByMarket().then((latestByMarket) => {
      if (cancelled) return;
      const latestYmd = getLatestTradeDateForScreenerMarket(latestByMarket, selectedMarket);
      if (!latestYmd || selectedYmd !== latestYmd) return;
      intervalId = setInterval(runRefresh, STOCKS_DISPLAY_REFRESH_MS);
    }).catch(() => {});

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [tradeDates, selectedDate, selectedMarket, isInitialLoad, fetchStocksData, tradeDateFromYmd]);

  const selectedDateRef = useRef(selectedDate);
  const selectedMarketRef = useRef(selectedMarket);
  selectedDateRef.current = selectedDate;
  selectedMarketRef.current = selectedMarket;

  useLiveMarketDisplay({
    enabled: Boolean(user) && allStocksData.length > 0 && Boolean(selectedDate) && !isInitialLoad,
    onApply: (live) => {
      const selectedYmd = selectedDateRef.current?.toISOString().split('T')[0];
      // Regular quotes only when the screener shows today's data; extended-hours
      // quotes always merge (premarket runs before today's trade date exists).
      const quotes = shouldApplyScreenerLiveQuotes(selectedYmd) ? live.quotes : null;
      const extQuotes = live.extendedQuotes && Object.keys(live.extendedQuotes).length > 0
        ? live.extendedQuotes
        : null;
      if (!quotes && !extQuotes) return;
      setAllStocksData((prev) => applyLiveQuotesToStocks(prev, quotes, extQuotes));
    }
  });

  useEffect(() => {
    const fetchNewsRatings = async () => {
      try {
        const currentDate = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        const newsData = await getCachedNewsData(currentDate, true);
        if (newsData && newsData.length > 0) {
          const ratingsMap = {};
          newsData.forEach(news => {
            if (news && news.symbol_id && news.rating != null && typeof news.rating === 'number') {
              const sid = news.symbol_id;
              if (ratingsMap[sid] === undefined || news.rating > ratingsMap[sid]) {
                ratingsMap[sid] = news.rating;
              }
            }
          });
          setNewsRatingsMap(ratingsMap);
        }
      } catch (error) {
        console.error('Error fetching news ratings for filter:', error);
      }
    };

    if (allStocksData.length > 0) {
      fetchNewsRatings();
    }
  }, [allStocksData]);

  const fetchTradeDates = async () => {
    try {
      const freshFetch = shouldUseFreshClientCache();
      const reloadScope = getClientCacheReloadScope();
      const dates = await getCachedTradeDates(!freshFetch);
      const parsedDates = dates.map(date => new Date(date + 'T00:00:00')).sort((a, b) => b - a);
      setTradeDates(parsedDates);

      const latestByMarket = await getCachedLatestTradeDatesByMarket(!freshFetch);
      setLatestTradeDatesByMarket(latestByMarket);

      const forceMarketLatest = freshFetch || reloadScope === 'global' || !selectedDate;
      if (forceMarketLatest && (latestByMarket.US || latestByMarket.CA)) {
        await applyMarketLatestTradeDate(selectedMarket, { fetchData: true });
        return;
      }

      if (!selectedDate && parsedDates.length > 0) {
        const lastSelectedDate = localStorage.getItem('selectedDate');
        if (lastSelectedDate) {
          setSelectedDate(new Date(lastSelectedDate));
        } else {
          const marketLatest = getLatestTradeDateForScreenerMarket(latestByMarket, selectedMarket);
          const defaultDate = marketLatest
            ? parsedDates.find((d) => d.toISOString().split('T')[0] === marketLatest) || parsedDates[0]
            : parsedDates[0];
          setSelectedDate(defaultDate);
          localStorage.setItem('selectedDate', defaultDate.toISOString());
        }
      }
    } catch (error) {
      console.error('Error fetching trade dates:', error);
    }
  };

  const fetchPortfolio = async (userEmail) => {
    try {
      const cachedMap = getCachedPortfolioMap(userEmail);
      if (cachedMap) {
        const allSymbols = Object.values(cachedMap).flatMap(p => Object.keys(p || {}));
        setPortfolio(allSymbols);
        return;
      }

      const response = await authFetch(`/api/portfolio/${userEmail}`);
      const data = await response.json();
      const portfolioMap = data.portfolioMap || { portfolio_1: data.portfolio || {} };
      if (portfolioMap && Object.keys(portfolioMap).length > 0) {
        setCachedPortfolioMap(userEmail, portfolioMap);
      }
      const allSymbols = Object.values(portfolioMap).flatMap(p => Object.keys(p || {}));
      setPortfolio(allSymbols);
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  };


  const fetchWatchlist = async (userEmail) => {
    try {
      const cachedMap = getCachedWatchlistMap(userEmail);
      if (cachedMap) {
        const allSymbols = Object.values(cachedMap).flat();
        setWatchlist(allSymbols);
        return;
      }

      const response = await authFetch(`/api/watchlist/${userEmail}`);
      const data = await response.json();
      const watchlistMap = data.watchlistMap || { watchlist_1: data.watchlist || [] };
      if (watchlistMap && Object.keys(watchlistMap).length > 0) {
        setCachedWatchlistMap(userEmail, watchlistMap);
      }
      const allSymbols = Object.values(watchlistMap).flat();
      setWatchlist(allSymbols);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
    }
  };

  const fetchCustomRules = async (userEmail) => {
    try {
      const response = await authFetch(`/api/rules/${userEmail}`);
      if (!response.ok) {
        console.warn('[SCREENER] Could not load custom rules:', response.status);
        return;
      }
      const data = await response.json();
      if (data.rules) {
        setCustomRules(data.rules);
      }
    } catch (error) {
      console.error('Error fetching custom rules:', error);
    }
  };

  const saveCustomRules = async (rules) => {
    if (!user) return;
    
    try {
      const response = await authFetch(`/api/rules/${user.email}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rules }),
      });
      
      if (response.ok) {
        setCustomRules(rules);
      }
    } catch (error) {
      console.error('Error saving custom rules:', error);
    }
  };

  const handleFilterChange = (filters, newComparisons) => {
    setCurrentFilters(filters);
    setComparisons(newComparisons);
    applyFilters(filters, searchQuery, newComparisons);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    const mapKey = resolveSymbolMapKey(query || '', symbolMap || {});
    const isValidSymbol = !!mapKey;
    const matchedCompanyName = mapKey
      ? normalizeSymbolNameEntry(mapKey, symbolMap[mapKey]).company_name
      : null;
    let resolvedSymbol = null;
    let resolvedCompany = null;
    
    if (isValidSymbol && mapKey) {
      resolvedSymbol = mapKey;
      resolvedCompany = matchedCompanyName;
      setSelectedSymbol(mapKey);
      setSelectedCompanyName(matchedCompanyName);
    } else {
      // Check if query matches a company name exactly
      const companyMatch = Object.entries(symbolMap || {}).find(([sid, raw]) => {
        const m = normalizeSymbolNameEntry(sid, raw);
        const c = m.company_name;
        return (
          c &&
          (c.toUpperCase() === (query || '').trim().toUpperCase() ||
            c.toLowerCase() === (query || '').trim().toLowerCase() ||
            c === (query || '').trim())
        );
      });

      if (companyMatch) {
        const co = normalizeSymbolNameEntry(companyMatch[0], companyMatch[1]).company_name;
        resolvedSymbol = companyMatch[0];
        resolvedCompany = co;
        setSelectedSymbol(companyMatch[0]);
        setSelectedCompanyName(co);
      } else {
        // Not a valid symbol or exact company match, clear selection
        setSelectedSymbol(null);
        setSelectedCompanyName(null);
      }
    }
    
    // Pass resolved symbol/company so applyFilters uses them immediately (state updates are async)
    applyFilters(currentFilters, query, comparisons, { symbol: resolvedSymbol, company: resolvedCompany });
  };

  const handleSymbolSelect = (symbolId, company) => {
    const key = resolveSymbolMapKey(symbolId || '', symbolMap || {}) || symbolId.trim();
    setSelectedSymbol(key);
    setSelectedCompanyName(company);
    setSearchQuery('');
    applyFilters(currentFilters, '', comparisons, { symbol: key, company });
  };

  /** @deprecated Use resolveMarketForStock(stock) for row-level market; kept for raw market field reads */
  const normalizeStockMarket = (marketRaw) => {
    const m = (marketRaw || '').toString().trim().toUpperCase();
    if (!m) return '';
    if (m === 'US' || m === 'USA' || m.includes('UNITED STATES')) return 'US';
    if (m === 'CA' || m === 'CAN' || m.includes('CANADA')) return 'CA';
    return m;
  };

  const applyFilters = (filters, query, minMaxFilters, overrideSelection = null, indexFilterOverride) => {
    // Select the appropriate base data to filter
    // When custom rules are active, we should apply filters to the custom-rules-processed data
    const rawBase = useCustomRules && customRules
      ? applyCustomRules(allStocksData, customRules)
      : allStocksData;
    const baseData = Array.isArray(rawBase) ? rawBase : [];
    
    // Use override when provided (e.g. from handleSearch/handleSymbolSelect) so we don't rely on
    // state that hasn't updated yet (React setState is async).
    const effectiveSymbol = overrideSelection != null ? overrideSelection.symbol : selectedSymbol;
    const queryKey = resolveSymbolMapKey(query || '', symbolMap || {});
    const symbolToMatch = effectiveSymbol || queryKey || null;

    const effectiveIndex =
      indexFilterOverride !== undefined ? indexFilterOverride : indexFilter;

    const newFilteredData = baseData.filter(stock => {
      // Match search query / selected symbol (selection can remain after input is cleared)
      let matchesQuery = false;
      const name = (stock.name || '').toLowerCase();
      const q = (query || '').trim();
      const qLower = q.toLowerCase();
      
      if (symbolToMatch) {
        matchesQuery = (stock.symbol_id || '') === symbolToMatch;
      } else if (q === '') {
        matchesQuery = true;
      } else {
        matchesQuery =
          (stock.symbol_id || '').toLowerCase().includes(qLower) ||
          (stock.symbol || '').toLowerCase().includes(qLower) ||
          name.includes(qLower);
      }

      // Match basic filters (sector, symbol, name)
      const matchesBasicFilters = Object.keys(filters).every(key => {
        if (filters[key] === '') return true;
        if (key === 'sector') return stock[key] === filters[key];
        if (key === 'last_action') return stock[lastActionField] === filters[key].toLowerCase();
        if (key === 'instrument') return stock[key] === filters[key];
        if (key === 'symbol' || key === 'name') {
          return stock[key].toLowerCase().includes(filters[key].toLowerCase());
        }
        return true;
      });

      // Match range filters
      const matchesRangeFilters = Object.entries(minMaxFilters).every(([key, range]) => {
        if (!range) return true;

        let stockValue;
        if (key === 'news_rating') {
          const rawRating = newsRatingsMap[stock.symbol_id];
          const hasFilter = (range.min !== undefined && range.min !== '') ||
                            (range.max !== undefined && range.max !== '');
          if (rawRating == null) return !hasFilter;
          stockValue = Math.round(rawRating);
        } else {
          const dataKey = key === 'take_profit' ? takeProfitField : key;
          stockValue = parseFloat(stock[dataKey]);
          if (isNaN(stockValue)) return true;
          if (key === 'market_cap') stockValue = stockValue / 1e6;
        }

        const min = range.min !== undefined && range.min !== '' ? parseFloat(range.min) : null;
        const max = range.max !== undefined && range.max !== '' ? parseFloat(range.max) : null;

        if (min !== null && (max === null || isNaN(max))) {
          return stockValue >= min;
        }
        if (max !== null && (min === null || isNaN(min))) {
          return stockValue <= max;
        }
        if (min !== null && max !== null) {
          return stockValue >= min && stockValue <= max;
        }
        return true;
      });

      // Index filter
      let matchesIndex = true;
      if (effectiveIndex === 'all') {
        const raw = String(stock.index ?? '').trim();
        const slug = indexFilterSlug(stock.index);
        matchesIndex =
          raw !== '' &&
          raw.toLowerCase() !== 'none' &&
          slug.length > 0;
      } else if (effectiveIndex !== '') {
        const hay = indexFilterSlug(stock.index);
        const needle = indexFilterSlug(effectiveIndex);
        matchesIndex = needle.length > 0 && hay.includes(needle);
      }

      // Pattern filter
      let matchesPattern = true;
      if (patternFilter !== '') {
        matchesPattern = stock.chart_pattern === patternFilter;
      }

      // Market filter
      let matchesMarket = true;
      if (selectedMarket !== '') {
        matchesMarket = resolveMarketForStock(stock) === selectedMarket;
      }

      // Market-cap size buckets (none selected = all)
      let matchesMarketCap = true;
      if (selectedMarketCaps.length > 0) {
        matchesMarketCap = selectedMarketCaps.some((key) => {
          const bucket = MARKET_CAP_BUCKETS.find((b) => b.key === key);
          return bucket ? stockMatchesMarketCapBucket(stock.market_cap, bucket) : false;
        });
      }

      return matchesQuery && matchesBasicFilters && matchesRangeFilters && matchesIndex && matchesPattern && matchesMarket && matchesMarketCap;
    });
    
    setFilteredData(newFilteredData);

    if (symbolToMatch && selectedMarket !== '') {
      const stock = baseData.find((s) => (s.symbol_id || '') === symbolToMatch);
      if (stock) {
        const stockMarket = resolveMarketForStock(stock);
        if (stockMarket && stockMarket !== selectedMarket) {
          setScreenerSymbolMarketNotice({
            symbolId: symbolToMatch,
            symbolLabel: stock.symbol || symbolToMatch,
            stockMarket,
          });
        } else {
          setScreenerSymbolMarketNotice(null);
        }
      } else {
        setScreenerSymbolMarketNotice(null);
      }
    } else {
      setScreenerSymbolMarketNotice(null);
    }

    if (symbolToMatch && newFilteredData.length > 0) {
      const matchedStock = newFilteredData.find(s => s.symbol_id === symbolToMatch);
      if (matchedStock) {
        const status = (useCustomRules
          ? matchedStock.new_status
          : matchedStock[signalField]
        || '').toLowerCase();
        if (status === 'buy') {
          setActiveSignalTab('buy');
        } else if (status === 'sell') {
          setActiveSignalTab('sell');
        } else {
          setActiveSignalTab('hold');
        }
      }
    }
  };

  const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];

  const buySignal = useMemo(() => {
    return safeFilteredData.filter(stock =>
      useCustomRules
        ? stock.new_status?.toLowerCase() === 'buy'
        : stock[signalField]?.toLowerCase() === 'buy'
    );
  }, [safeFilteredData, useCustomRules, signalField]);

  const sellSignal = useMemo(() => {
    return safeFilteredData.filter(stock =>
      useCustomRules
        ? stock.new_status?.toLowerCase() === 'sell'
        : stock[signalField]?.toLowerCase() === 'sell'
    );
  }, [safeFilteredData, useCustomRules, signalField]);

  const holdSignal = useMemo(() => {
    return safeFilteredData.filter(stock =>
      useCustomRules
        ? stock.new_status?.toLowerCase() === 'hold'
        : stock[signalField]?.toLowerCase() === 'hold'
    );
  }, [safeFilteredData, useCustomRules, signalField]);

  const screenerModalFilterChips = useMemo(() => {
    const chips = [];
    if (searchQuery.trim()) {
      chips.push({ key: 'search', label: 'Search', value: searchQuery.trim() });
    } else if (selectedSymbol) {
      const tick =
        normalizeSymbolNameEntry(selectedSymbol, symbolMap?.[selectedSymbol]).symbol ||
        selectedSymbol;
      chips.push({
        key: 'search',
        label: 'Search',
        value: selectedCompanyName ? `${tick} — ${selectedCompanyName}` : tick,
      });
    }
    FILTER_MODAL_KEYS.forEach((key) => {
      const val = currentFilters[key];
      if (val !== '' && val != null) {
        if (key === 'last_action') {
          chips.push({ key, label: FILTER_LABELS[key], value: String(val).charAt(0).toUpperCase() + String(val).slice(1) });
        } else if (key === 'sector') {
          chips.push({ key, label: 'Sector', value: val });
        } else {
          chips.push({ key, label: FILTER_LABELS[key] || key, value: val });
        }
      }
    });
    Object.entries(comparisons).forEach(([key, range]) => {
      const formatted = formatScreenerRange(range);
      if (formatted) {
        chips.push({ key: `range-${key}`, label: FILTER_LABELS[key] || key, value: formatted });
      }
    });
    selectedMarketCaps.forEach((key) => {
      const bucket = MARKET_CAP_BUCKETS.find((b) => b.key === key);
      if (bucket) {
        chips.push({ key: `mcap-${key}`, label: 'Market Cap', value: bucket.label });
      }
    });
    if (useCustomRules) {
      chips.push({ key: 'custom-rules', label: 'Rules', value: 'Custom' });
    }
    return chips;
  }, [searchQuery, selectedSymbol, selectedCompanyName, symbolMap, currentFilters, comparisons, selectedMarketCaps, useCustomRules]);

  const hasModalFilters = screenerModalFilterChips.length > 0;

  const instrumentSelection = currentFilters.instrument ?? SCREENER_VIEW_DEFAULTS.instrument;

  const isScreenerViewNonDefault =
    patternFilter !== SCREENER_VIEW_DEFAULTS.patternFilter ||
    indexFilter !== SCREENER_VIEW_DEFAULTS.indexFilter ||
    selectedMarket !== SCREENER_VIEW_DEFAULTS.market ||
    instrumentSelection !== SCREENER_VIEW_DEFAULTS.instrument;

  const screenerUniverseCount = allStocksData.length;

  const screenerDateNoticeOptions = useMemo(() => {
    if (!screenerDateNotice) return [];
    const {
      type,
      marketLatest,
      marketLabel,
      marketKey,
      selectedDate: sel,
      isOtherMarketLatest,
      otherMarketLabel,
    } = screenerDateNotice;
    if (type === 'wrong-date' && marketLatest) {
      const marketShort = marketKey === 'US' ? 'US' : marketKey === 'CA' ? 'Canada' : marketLabel;
      return [{
        key: 'use-market-latest-date',
        label: isOtherMarketLatest
          ? `Use ${marketShort} latest trade date (${marketLatest})`
          : `Use latest trade date for ${marketLabel}`,
        detail: isOtherMarketLatest
          ? `${sel} is ${otherMarketLabel}'s latest session — no ${marketLabel} data that day`
          : `Switch from ${sel} to ${marketLatest}`,
        action: 'use-market-latest-date',
      }];
    }
    if (type === 'no-data' && marketLatest && sel === marketLatest) {
      return [{
        key: 'refresh-screener',
        label: 'Refresh screener',
        detail: `Retry loading data for ${marketLatest} (${marketLabel})`,
        action: 'refresh-screener',
      }];
    }
    if (type === 'no-data' && marketLatest && sel !== marketLatest) {
      const marketShort = marketKey === 'US' ? 'US' : marketKey === 'CA' ? 'Canada' : marketLabel;
      return [{
        key: 'use-market-latest-date',
        label: isOtherMarketLatest
          ? `Use ${marketShort} latest trade date (${marketLatest})`
          : `Use latest trade date for ${marketLabel}`,
        detail: isOtherMarketLatest
          ? `${sel} is ${otherMarketLabel}'s latest — switch to ${marketLatest} for ${marketLabel}`
          : `Load ${marketLatest} instead of ${sel}`,
        action: 'use-market-latest-date',
      }];
    }
    return [];
  }, [screenerDateNotice]);

  const screenerSymbolMarketOptions = useMemo(() => {
    if (!screenerSymbolMarketNotice) return [];
    const { stockMarket, symbolLabel } = screenerSymbolMarketNotice;
    const opts = [];
    if (selectedMarket !== '') {
      opts.push({
        key: 'symbol-market-all',
        label: 'All Markets',
        detail: `Include ${symbolLabel} from US and Canada`,
        action: 'market-all',
      });
    }
    if (stockMarket === 'US' && selectedMarket !== 'US') {
      opts.push({
        key: 'symbol-market-us',
        label: 'United States',
        detail: `${symbolLabel} is a US symbol`,
        action: 'market-us',
      });
    }
    if (stockMarket === 'CA' && selectedMarket !== 'CA') {
      opts.push({
        key: 'symbol-market-ca',
        label: 'Canada',
        detail: `${symbolLabel} is a Canada symbol`,
        action: 'market-ca',
      });
    }
    return opts;
  }, [screenerSymbolMarketNotice, selectedMarket]);

  const screenerBroadenOptions = useMemo(() => {
    const options = [];
    const indexOpts = getScreenerIndexFilterOptions(selectedMarket);
    const indexLabel = indexOpts.find((o) => o.value === indexFilter)?.label;

    if (patternFilter) {
      options.push({
        key: 'clear-pattern',
        label: 'Any pattern',
        detail: `Clear “${PATTERN_FILTER_LABELS[patternFilter] || patternFilter}” (Any Pattern)`,
        action: 'pattern',
      });
    }

    if (selectedMarketCaps.length > 0) {
      const labels = selectedMarketCaps
        .map((k) => MARKET_CAP_BUCKETS.find((b) => b.key === k)?.label)
        .filter(Boolean)
        .join(', ');
      options.push({
        key: 'clear-market-cap',
        label: 'All market caps',
        detail: `Clear “${labels}” size filter`,
        action: 'market-cap-all',
      });
    }

    if (indexFilter === 'all') {
      options.push({
        key: 'index-all-symbols',
        label: 'All symbols',
        detail: 'Stop limiting to index members — broadest index setting',
        action: 'index-empty',
      });
    } else if (indexFilter !== '') {
      options.push({
        key: 'index-all-indices',
        label: 'All indices',
        detail: `Clear “${indexLabel || indexFilter}” → default All Indices`,
        action: 'index-all',
      });
      options.push({
        key: 'index-all-symbols',
        label: 'All symbols',
        detail: 'Remove index filter entirely',
        action: 'index-empty',
      });
    }

    if (instrumentSelection === '') {
      options.push({
        key: 'instrument-stock',
        label: 'Stocks tab',
        detail: 'Switch from All to default Stocks view',
        action: 'instrument-stock',
      });
    } else if (instrumentSelection !== SCREENER_VIEW_DEFAULTS.instrument) {
      options.push({
        key: 'instrument-stock',
        label: 'Stocks tab',
        detail: `Switch from ${instrumentSelection} to default Stocks`,
        action: 'instrument-stock',
      });
    }

    if (selectedMarket === 'CA') {
      options.push({
        key: 'market-us',
        label: 'United States',
        detail: 'Default market with US index options',
        action: 'market-us',
      });
      options.push({
        key: 'market-all',
        label: 'All markets',
        detail: 'Include US and Canada symbols',
        action: 'market-all',
      });
    } else if (selectedMarket === '') {
      options.push({
        key: 'market-us',
        label: 'United States',
        detail: 'Default US market view',
        action: 'market-us',
      });
    } else if (selectedMarket !== SCREENER_VIEW_DEFAULTS.market) {
      options.push({
        key: 'market-us',
        label: 'United States',
        detail: 'Reset to default market',
        action: 'market-us',
      });
    }

    if (screenerSymbolMarketOptions.length > 0) {
      return [...screenerSymbolMarketOptions, ...options];
    }
    if (screenerDateNoticeOptions.length > 0) {
      return [...screenerDateNoticeOptions, ...options];
    }
    return options;
  }, [patternFilter, indexFilter, selectedMarket, instrumentSelection, selectedMarketCaps, screenerDateNoticeOptions, screenerSymbolMarketOptions]);

  /** Clears Filters dialog values, min/max ranges, search, and market-cap size filters — keeps date, strategy, index, patterns, market, and instrument tab. */
  const resetFilters = () => {
    const preservedInstrument = currentFilters.instrument;
    const clearedModal = FILTER_MODAL_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {});
    const nextFilters = { ...clearedModal, instrument: preservedInstrument };
    setCurrentFilters(nextFilters);
    setComparisons({});
    setSearchQuery('');
    setSelectedSymbol(null);
    setSelectedCompanyName(null);
    setSelectedMarketCaps([]);
    applyFilters(nextFilters, '', {});
  };

  /** Remove a single active filter chip (search, modal filter, min/max range, market cap, or custom rules). */
  const removeScreenerChip = (chipKey) => {
    if (chipKey === 'search') {
      setSearchQuery('');
      setSelectedSymbol(null);
      setSelectedCompanyName(null);
      applyFilters(currentFilters, '', comparisons, { symbol: null, company: null });
      return;
    }
    if (chipKey === 'custom-rules') {
      setUseCustomRules(false);
      return;
    }
    if (chipKey.startsWith('mcap-')) {
      const bucketKey = chipKey.slice('mcap-'.length);
      setSelectedMarketCaps((prev) => prev.filter((k) => k !== bucketKey));
      return;
    }
    if (chipKey.startsWith('range-')) {
      const rangeKey = chipKey.slice('range-'.length);
      const nextComparisons = { ...comparisons };
      delete nextComparisons[rangeKey];
      setComparisons(nextComparisons);
      applyFilters(currentFilters, searchQuery, nextComparisons);
      return;
    }
    const nextFilters = { ...currentFilters, [chipKey]: '' };
    setCurrentFilters(nextFilters);
    applyFilters(nextFilters, searchQuery, comparisons);
  };

  const handleRefreshScreener = async () => {
    setIsRefreshing(true);
    try {
      await fetchTradeDates();
      const refreshTasks = [];
      if (selectedDate) {
        refreshTasks.push(fetchStocksData(selectedDate, true));
      }
      if (user?.email) {
        refreshTasks.push(
          refreshFilterPresetsFromServer(user.email).catch((error) => {
            console.warn('[FILTER PRESETS] Error refreshing presets on screener refresh:', error);
          })
        );
      }
      await Promise.all(refreshTasks);
    } catch (error) {
      console.error('Error refreshing screener:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleRuleMode = () => {
    setUseCustomRules((prev) => !prev);
  };

  const toggleMarketCapBucket = (key) => {
    setSelectedMarketCaps((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  useEffect(() => {
    const refreshDataInBackground = () => {
      if (selectedDate) {
        fetchStocksData(selectedDate);
      }
    };

    // Set up 60-minute interval for background refresh
    const refreshInterval = setInterval(refreshDataInBackground, 7 * 60 * 60 * 1000);

    // Clean up interval on unmount
    return () => clearInterval(refreshInterval);
  }, [selectedDate, fetchStocksData]);

  useEffect(() => {
    // When stock data loads or refreshes, always re-apply screener filters (market, index, pattern,
    // search, etc.). Previously we only called applyFilters when modal filters were set, which left
    // filteredData as the full universe and broke market switching after refresh.
    if (!isLoading && allStocksData.length > 0) {
      applyFilters(currentFilters, searchQuery, comparisons);
    }
  }, [allStocksData, isLoading, useCustomRules, customRules]);

  useEffect(() => {
    if (!isLoading && allStocksData.length > 0) {
      applyFilters(currentFilters, searchQuery, comparisons);
    }
  }, [strategy]);

  useEffect(() => {
    if (!isLoading && allStocksData.length > 0) {
      applyFilters(currentFilters, searchQuery, comparisons);
    }
  }, [indexFilter, patternFilter, selectedMarket, selectedMarketCaps]);

  // Market view: missing rows on selected date → offer 1-click switch to this market's latest
  useEffect(() => {
    if (isLoading || !selectedDate) return;
    if (!latestTradeDatesByMarket?.US && !latestTradeDatesByMarket?.CA) return;

    const ymd = selectedDate.toISOString().split('T')[0];
    const notice = buildScreenerDateNotice(ymd, selectedMarket, latestTradeDatesByMarket, allStocksData);
    if (notice) {
      setScreenerDateNotice(notice);
    } else if (allStocksData.length > 0) {
      setScreenerDateNotice(null);
    }
  }, [isLoading, selectedDate, selectedMarket, allStocksData, latestTradeDatesByMarket]);

  // Close dashboard help when clicking outside
  useEffect(() => {
    if (!showDashboardHelp) return;
    const handleClickOutside = (e) => {
      if (dashboardHelpRef.current && !dashboardHelpRef.current.contains(e.target)) {
        setShowDashboardHelp(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDashboardHelp]);

  const instrumentTabs = [
    { key: '', label: 'All' },
    { key: 'Stock', label: 'Stocks' },
    { key: 'ETF', label: 'ETF' },
    { key: 'Fund', label: 'Funds' },
  ];

  const marketOptions = [
    { key: '', label: 'All Markets' },
    { key: 'US', label: 'United States' },
    { key: 'CA', label: 'Canada' },
  ];

  const handleInstrumentTab = (instrumentKey) => {
    const updatedFilters = { ...currentFilters, instrument: instrumentKey };
    setCurrentFilters(updatedFilters);
    if (instrumentKey === '') {
      setIndexFilter('');
      applyFilters(updatedFilters, searchQuery, comparisons, null, '');
    } else {
      applyFilters(updatedFilters, searchQuery, comparisons);
    }
  };

  const handleMarketChange = async (marketKey) => {
    setSelectedMarket(marketKey);
    if (marketKey === 'CA') {
      setIndexFilter('spTSX');
    } else if (marketKey === 'US') {
      setIndexFilter('all');
    } else {
      setIndexFilter('');
    }

    // Keep the currently selected date if it has rows for the new market.
    // Only jump to the market's latest trade date when the selected date has no data.
    const hasSelectedDate = Boolean(selectedDate);
    const data = Array.isArray(allStocksData) ? allStocksData : [];
    const hasAnyRows = data.length > 0;
    const hasRowsForMarket =
      marketKey === '' || marketKey == null
        ? hasAnyRows
        : data.some((s) => resolveMarketForStock(s) === marketKey);

    if (!hasSelectedDate || !hasRowsForMarket) {
      await applyMarketLatestTradeDate(marketKey);
    }
  };

  const handleUseMarketLatestTradeDate = () => {
    applyMarketLatestTradeDate(selectedMarket);
  };

  const applyScreenerBroadenOption = (action) => {
    switch (action) {
      case 'pattern':
        setPatternFilter('');
        break;
      case 'market-cap-all':
        setSelectedMarketCaps([]);
        break;
      case 'index-all':
        setIndexFilter('all');
        break;
      case 'index-empty':
        setIndexFilter('');
        break;
      case 'instrument-stock':
        handleInstrumentTab('Stock');
        return;
      case 'market-us':
        handleMarketChange('US');
        return;
      case 'market-ca':
        handleMarketChange('CA');
        return;
      case 'market-all':
        setSelectedMarket('');
        setIndexFilter('');
        applyMarketLatestTradeDate('');
        return;
      case 'use-market-latest-date':
        handleUseMarketLatestTradeDate();
        return;
      case 'refresh-screener':
        if (selectedDate) handleRefreshScreener();
        return;
      default:
        break;
    }
  };

  const resetScreenerViewToDefaults = () => {
    setPatternFilter(SCREENER_VIEW_DEFAULTS.patternFilter);
    setSelectedMarket(SCREENER_VIEW_DEFAULTS.market);
    setIndexFilter(SCREENER_VIEW_DEFAULTS.indexFilter);
    const nextFilters = { ...currentFilters, instrument: SCREENER_VIEW_DEFAULTS.instrument };
    setCurrentFilters(nextFilters);
    applyFilters(nextFilters, searchQuery, comparisons);
  };

  return (
    <div className="container">
      <main className="screener-page">
        <div className="screener-head">
          <div className="screener-head-text">
            <h2 className="screener-head-title">Stock Screener</h2>
            <p className="screener-head-sub">
              Scan Buy, Sell, and Hold signals by market, index, fundamentals, and technicals.
            </p>
          </div>
          <div className="screener-market-dropdown">
            <Globe size={16} className="screener-market-icon" aria-hidden="true" />
            <select
              value={selectedMarket}
              onChange={e => handleMarketChange(e.target.value)}
              className="screener-market-select"
              aria-label="Market"
            >
              {marketOptions.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="screener-market-chevron" aria-hidden="true" />
          </div>
        </div>

        <div className="screener-toolbar">
          <div className="screener-toolbar-top">
            <div className="screener-seg" role="tablist" aria-label="Asset type">
              {instrumentTabs.map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={currentFilters.instrument === tab.key}
                  className={`screener-seg-btn${currentFilters.instrument === tab.key ? ' active' : ''}`}
                  onClick={() => handleInstrumentTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="screener-search">
              <Search size={16} className="screener-search-icon" aria-hidden="true" />
              <SymbolAutocomplete
                value={searchQuery}
                onChange={(value) => handleSearch(value)}
                onSelect={handleSymbolSelect}
                placeholder="Search symbol or company"
                className="screener-search-input"
                showValidation={false}
                maxSuggestions={10}
                clearOnSelect
              />
            </div>
          </div>

          <div className="screener-fields">
            <div className="screener-field">
              <label htmlFor="trade-date-picker" className="screener-field-label">Trade Date</label>
              <div className="screener-field-input">
                <DatePicker
                  id="trade-date-picker"
                  selected={selectedDate}
                  onChange={date => {
                    const tradeDate = new Date(date);
                    tradeDate.setHours(12, 0, 0, 0);
                    setSelectedDate(tradeDate);
                    localStorage.setItem('selectedDate', tradeDate.toISOString());
                  }}
                  includeDates={tradeDates}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                  filterDate={date => tradeDates.some(d => d.toDateString() === date.toDateString())}
                  shouldCloseOnSelect={true}
                  showMonthDropdown
                  showYearDropdown
                  className="screener-select"
                />
                <ChevronDown size={14} className="screener-field-chevron" aria-hidden="true" />
              </div>
            </div>
            <div className="screener-field">
              <label htmlFor="strategy-selector" className="screener-field-label">Strategy</label>
              <div className="screener-field-input">
                <select
                  id="strategy-selector"
                  value={strategy}
                  onChange={e => setStrategy(e.target.value)}
                  className="screener-select"
                >
                  <option value="momentum">Momentum</option>
                  <option value="mean_reversion">Mean Reversion</option>
                </select>
                <ChevronDown size={14} className="screener-field-chevron" aria-hidden="true" />
              </div>
            </div>
            <div className="screener-field">
              <label htmlFor="index-filter" className="screener-field-label">Index</label>
              <div className="screener-field-input">
                <select
                  id="index-filter"
                  value={indexFilter}
                  onChange={e => setIndexFilter(e.target.value)}
                  className="screener-select"
                >
                  {getScreenerIndexFilterOptions(selectedMarket).map((opt, i) => (
                    <option key={`${selectedMarket}-${i}-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="screener-field-chevron" aria-hidden="true" />
              </div>
            </div>
            <div className="screener-field">
              <label htmlFor="pattern-filter" className="screener-field-label">Chart Pattern</label>
              <div className="screener-field-input">
                <select
                  id="pattern-filter"
                  value={patternFilter}
                  onChange={e => setPatternFilter(e.target.value)}
                  className="screener-select"
                >
                  <option value="">Any Pattern</option>
                  {/* <option value="flag">Flag</option>
                  <option value="wedge">Wedges</option>
                  <option value="triangle">Triangles</option>
                  <option value="head_shoulder">Head and Shoulder</option> */}
                  <option value="cup_handle">Cup & Handle</option>
                  <option value="double_top">Double Top</option>
                  <option value="double_bottom">Double Bottom</option>
                </select>
                <ChevronDown size={14} className="screener-field-chevron" aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="screener-toolbar-bottom">
            <div className="screener-actions screener-actions--left">
              <div className="screener-mcap-filters" role="group" aria-label="Market cap size filters">
                {MARKET_CAP_BUCKETS.map((bucket) => {
                  const active = selectedMarketCaps.includes(bucket.key);
                  return (
                    <button
                      key={bucket.key}
                      type="button"
                      className={`screener-mcap-pill${active ? ' is-active' : ''}`}
                      title={bucket.title}
                      aria-pressed={active}
                      onClick={() => toggleMarketCapBucket(bucket.key)}
                    >
                      <Check className="screener-mcap-check" size={14} aria-hidden="true" />
                      {bucket.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="screener-actions screener-actions--right">
              <div className="dashboard-help-wrap" ref={dashboardHelpRef}>
                <button
                  type="button"
                  onClick={() => setShowDashboardHelp(prev => !prev)}
                  className="dashboard-help-trigger"
                  title="How to use the dashboard"
                  aria-label="Dashboard help"
                >
                  <HelpCircle className="icon" size={20} />
                </button>
                {showDashboardHelp && (
                <div className="dashboard-help-popover">
                  <div className="dashboard-help-content">
                    <h4>Using the dashboard</h4>
                    <ul>
                      <li><strong>Trade Date</strong> — Pick a trading date to load that day’s signals.</li>
                      <li><strong>Default / Custom</strong> — Use <em>Default</em> for standard Buy/Sell/Hold rules, or <em>Custom</em> to apply your own rules (set via Add Rules).</li>
                      <li><strong>Advanced Filters</strong> — Filter by P/E Ratio, RSI, Sector, and other fields. Set min/max for numbers or choose options (e.g. Sector, Last Signal), then click <em>Apply Filters</em>. In the filter dialog: <em>Default</em> loads your saved presets, <em>Save</em> stores the current settings as your default (requires login).</li>
                      <li><strong>Add Rules</strong> — Define custom buy/sell rules (when Custom is on).</li>
                      <li><strong>List reset</strong> — Clears applied <em>Filters</em> criteria and search; date, strategy, index, patterns, market, and Stocks/ETF/Funds tabs stay as you set them.</li>
                      <li><strong>Column sort</strong> — Click any column header to sort. Each click cycles ascending → descending → neutral (back to symbol A–Z). The active column is highlighted; chevrons show direction.</li>
                    </ul>
                    <h4 className="dashboard-help-sub">Fields in the signal list</h4>
                    <ul className="dashboard-help-fields">
                      <li><strong>Symbol</strong> — Stock symbol (e.g. AAPL, MSFT).</li>
                      <li><strong>Company</strong> — Company name.</li>
                      <li><strong>Market Cap</strong> — Market capitalization (company size).</li>
                      <li><strong>Price</strong> — Closing price for the date.</li>
                      <li><strong>Volume</strong> — Trading volume.</li>
                      <li><strong>Updated At</strong> — Trade date/time for the signal.</li>
                      <li><strong>% Change</strong> — Percent change from previous close.</li>
                      <li><strong>Returns</strong> — Strategy return (take-profit %) for the selected strategy.</li>
                      <li><strong>First Bought</strong> — Date of the first buy signal for the selected strategy.</li>
                      {/* <li><strong>Performance</strong> — Hold performance (%) since first buy for the selected strategy.</li> */}
                      <li><strong>Signal</strong> — Buy, Sell, or Hold (Neutral).</li>
                      <li><strong>News</strong> — Latest news sentiment score for the symbol.</li>
                    </ul>
                    <h4 className="dashboard-help-sub">Advanced filter fields</h4>
                    <ul className="dashboard-help-fields">
                      <li><strong>P/E Ratio, RSI (14), Price, Volume, Market Cap (M)</strong> — Core fundamentals and liquidity.</li>
                      <li><strong>% Change (1D), Price vs MA(20) Diff (%),Volume vs MA(20) Diff (%)</strong> — Price and volume vs averages.</li>
                      <li><strong>Price vs 52W High Diff (%), Days Since 52W High</strong> — Distance from 52-week highs.</li>
                      <li><strong>Up Days (5D/10D/20D), Current Streak (Days)</strong> — Recent price-action streaks.</li>
                      <li><strong>Strategy Return (%), News Sentiment (0–10), Last Signal, Sector</strong> — Strategy, signals, and sentiment.</li>
                    </ul>
                    <p className="dashboard-help-hint">Hover over the <HelpCircle size={12} style={{ verticalAlign: 'middle' }} /> next to each field in Advanced Filters for min/max guidance.</p>
                  </div>
                  <button type="button" className="dashboard-help-close" onClick={() => setShowDashboardHelp(false)} aria-label="Close help">
                    <X size={14} />
                  </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleRefreshScreener}
                className="screener-icon-btn"
                title={user ? 'Refresh screener data and saved filter presets' : 'Refresh screener data'}
                aria-label="Refresh screener"
                disabled={isRefreshing}
              >
                <RefreshCw size={24} className={`icon${isRefreshing ? ' spinning' : ''}`} />
              </button>
              {customRules && (
                <div className="rule-mode-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={useCustomRules}
                      onChange={toggleRuleMode}
                    />
                    <div className="toggle-slider">
                      <span className="toggle-option default">Default</span>
                      <span className="toggle-option custom">Custom</span>
                    </div>
                  </label>
                </div>
              )}
              {user && (
                <button
                  onClick={() => setIsCustomRulesModalOpen(true)}
                  className="screener-btn screener-btn-outline"
                  title="Custom Trading Rules"
                >
                  <Settings className="icon" size={16} />
                  Add Rules
                </button>
              )}
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="screener-btn screener-btn-primary"
              >
                <SlidersHorizontal className="icon" size={24} />
                Filters
                {screenerModalFilterChips.length > 0 && (
                  <span className="screener-filter-count">{screenerModalFilterChips.length}</span>
                )}
              </button>
              <button
                onClick={resetFilters}
                className="screener-icon-btn"
                title="Undo search and filters"
                aria-label="Undo search and filters"
              >
                <Undo2 size={24} className="icon" />
              </button>
            </div>
          </div>

          {hasModalFilters && (
            <div className="screener-chips" aria-label="Active filters">
              <span className="screener-chips-label">Active:</span>
              {screenerModalFilterChips.map((chip) => (
                <span key={chip.key} className="screener-chip">
                  <span className="screener-chip-label">{chip.label}</span>
                  <span className="screener-chip-value">{chip.value}</span>
                  <button
                    type="button"
                    className="screener-chip-remove"
                    onClick={() => removeScreenerChip(chip.key)}
                    aria-label={`Remove ${chip.label} filter`}
                    title={`Remove ${chip.label} filter`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <button type="button" className="screener-chips-clear" onClick={resetFilters}>
                Clear all
              </button>
            </div>
          )}
        </div>

        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={handleFilterChange}
          currentFilters={currentFilters}
          comparisons={comparisons}
          user={user}
        />

        <CustomRulesModal
          isOpen={isCustomRulesModalOpen}
          onClose={() => setIsCustomRulesModalOpen(false)}
          onSave={saveCustomRules}
          currentRules={customRules}
        />

        {isLoading ? (
          <div className="screener-loading pw-loading" role="status" aria-live="polite">
            <div className="pw-spinner" aria-hidden="true" />
            <p>Loading screener data…</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="screener-empty-state" role="status" aria-live="polite">
            <div className="screener-empty-icon" aria-hidden="true">
              <SearchX size={26} strokeWidth={1.75} />
            </div>
            <h3 className="screener-empty-title">No matching stocks</h3>
            <p className="screener-empty-lead">
              {screenerSymbolMarketNotice
                ? `${screenerSymbolMarketNotice.symbolLabel} is in ${marketLabelForKey(screenerSymbolMarketNotice.stockMarket)} but the screener is set to ${marketLabelForKey(selectedMarket)}. Use the market buttons above or below to switch.`
                : screenerDateNotice?.isOtherMarketLatest
                ? `${screenerDateNotice.selectedDate} is ${screenerDateNotice.otherMarketLabel}'s latest trade date, but there is no ${screenerDateNotice.marketLabel} data that day. Pick an option below to switch to the latest ${screenerDateNotice.marketLabel} session (${screenerDateNotice.marketLatest}).`
                : screenerDateNotice?.type === 'wrong-date'
                ? `No ${screenerDateNotice.marketLabel} stock data for ${screenerDateNotice.selectedDate}. The latest trade date for this market is ${screenerDateNotice.marketLatest}.`
                : screenerDateNotice?.type === 'no-data'
                  ? `No stock data for ${screenerDateNotice.selectedDate} (${screenerDateNotice.marketLabel}). Try an option below or refresh the screener.`
                  : screenerUniverseCount === 0
                ? 'No stock data is available for the selected trading date. Try another date or refresh the screener.'
                : hasModalFilters && isScreenerViewNonDefault
                  ? 'Nothing matches your list filters and current view settings. Clear filters or reset selections below.'
                  : hasModalFilters
                    ? 'Nothing matches your list filters. Clear filters or open Adjust filters to broaden criteria.'
                    : isScreenerViewNonDefault
                      ? 'Nothing matches your current pattern, index, market, or instrument selection. Reset to defaults or pick a broader option below.'
                      : 'Nothing matches the default view for this date. Try All symbols to include stocks outside major indices.'}
            </p>
            {hasModalFilters && (
              <div className="screener-empty-filters">
                <span className="screener-empty-filters-label">List filters</span>
                <ul className="screener-empty-filter-chips">
                  {screenerModalFilterChips.map((chip) => (
                    <li key={chip.key} className="screener-empty-filter-chip">
                      <span className="screener-empty-filter-chip-label">{chip.label}</span>
                      <span className="screener-empty-filter-chip-value">{chip.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {screenerBroadenOptions.length > 0 && (
              <div className="screener-empty-broaden">
                <span className="screener-empty-filters-label">Broaden your view</span>
                <ul className="screener-empty-broaden-list">
                  {screenerBroadenOptions.map((opt) => (
                    <li key={opt.key}>
                      <button
                        type="button"
                        className="screener-empty-broaden-btn"
                        onClick={() => applyScreenerBroadenOption(opt.action)}
                      >
                        <span className="screener-empty-broaden-label">{opt.label}</span>
                        <span className="screener-empty-broaden-detail">{opt.detail}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {screenerUniverseCount > 0 && (
              <p className="screener-empty-meta">
                {screenerUniverseCount.toLocaleString()} symbols loaded for this date — 0 match your current view.
              </p>
            )}
            <div className="screener-empty-actions">
              {hasModalFilters && (
                <button type="button" className="pw-btn pw-btn-primary" onClick={resetFilters}>
                  <Undo2 size={16} aria-hidden="true" />
                  Clear list filters
                </button>
              )}
              {isScreenerViewNonDefault && (
                <button type="button" className={`pw-btn ${hasModalFilters ? 'pw-btn-ghost' : 'pw-btn-primary'}`} onClick={resetScreenerViewToDefaults}>
                  <Undo2 size={16} aria-hidden="true" />
                  Reset view to defaults
                </button>
              )}
              {hasModalFilters && (
                <button type="button" className="pw-btn pw-btn-ghost" onClick={() => setIsFilterModalOpen(true)}>
                  <SlidersHorizontal size={16} aria-hidden="true" />
                  Adjust filters
                </button>
              )}
              {!hasModalFilters && screenerBroadenOptions.length === 0 && (
                <button type="button" className="pw-btn pw-btn-primary" onClick={() => applyScreenerBroadenOption('index-empty')}>
                  Show all symbols
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="screener-results-bar">
              <p className="screener-results-count">
                <strong>{safeFilteredData.length.toLocaleString()}</strong>
                {' '}of {screenerUniverseCount.toLocaleString()} symbols match your view
              </p>
              <div className="screener-results-meta">
                {selectedDate && (
                  <span className="screener-results-tag" title="Trade date">
                    {selectedDate.toISOString().split('T')[0]}
                  </span>
                )}
                <span className="screener-results-tag" title="Strategy">
                  {strategy === 'momentum' ? 'Momentum' : 'Mean Reversion'}
                </span>
                {useCustomRules && (
                  <span className="screener-results-tag screener-results-tag--rules" title="Custom rules applied">
                    Custom rules
                  </span>
                )}
              </div>
            </div>
            <div className="signal-tabs">
              <button
                className={`signal-tab signal-tab-buy${activeSignalTab === 'buy' ? ' active' : ''}`}
                onClick={() => setActiveSignalTab('buy')}
              >
                Buy <span className="signal-tab-count">{buySignal.length}</span>
              </button>
              <button
                className={`signal-tab signal-tab-sell${activeSignalTab === 'sell' ? ' active' : ''}`}
                onClick={() => setActiveSignalTab('sell')}
              >
                Sell <span className="signal-tab-count">{sellSignal.length}</span>
              </button>
              <button
                className={`signal-tab signal-tab-hold${activeSignalTab === 'hold' ? ' active' : ''}`}
                onClick={() => setActiveSignalTab('hold')}
              >
                Hold <span className="signal-tab-count">{holdSignal.length}</span>
              </button>
            </div>
            {activeSignalTab === 'buy' && (
              buySignal.length > 0
                ? <StockList data={buySignal} type="buy" user={user} signalField={signalField} takeProfitField={takeProfitField} holdProfitField={holdProfitField} firstBuyDateField={firstBuyDateField} useCustomRules={useCustomRules} />
                : (
                  <div className="signal-empty screener-signal-empty screener-signal-empty--buy" role="status">
                    <p className="screener-signal-empty-title">No Buy signals</p>
                    <p className="screener-signal-empty-desc">Stocks matching your filters don&apos;t have a Buy signal for the {strategy === 'momentum' ? 'Momentum' : 'Mean Reversion'} strategy on this date.</p>
                  </div>
                )
            )}
            {activeSignalTab === 'sell' && (
              sellSignal.length > 0
                ? <StockList data={sellSignal} type="sell" user={user} signalField={signalField} takeProfitField={takeProfitField} holdProfitField={holdProfitField} firstBuyDateField={firstBuyDateField} useCustomRules={useCustomRules} />
                : (
                  <div className="signal-empty screener-signal-empty screener-signal-empty--sell" role="status">
                    <p className="screener-signal-empty-title">No Sell signals</p>
                    <p className="screener-signal-empty-desc">Stocks matching your filters don&apos;t have a Sell signal for the {strategy === 'momentum' ? 'Momentum' : 'Mean Reversion'} strategy on this date.</p>
                  </div>
                )
            )}
            {activeSignalTab === 'hold' && (
              holdSignal.length > 0
                ? <StockList data={holdSignal} type="hold" user={user} signalField={signalField} takeProfitField={takeProfitField} holdProfitField={holdProfitField} firstBuyDateField={firstBuyDateField} useCustomRules={useCustomRules} />
                : (
                  <div className="signal-empty screener-signal-empty screener-signal-empty--hold" role="status">
                    <p className="screener-signal-empty-title">No Hold signals</p>
                    <p className="screener-signal-empty-desc">Stocks matching your filters don&apos;t have a Hold signal for the {strategy === 'momentum' ? 'Momentum' : 'Mean Reversion'} strategy on this date.</p>
                  </div>
                )
            )}
          </>
        )}
      </main>
    </div>
  );
};

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isAdminUser = (user) => user?.role === 'admin';

/** Wait for session bootstrap before admin gate — avoids redirect on first paint. */
const AdminRoute = ({ user, sessionChecked, children }) => {
  if (!sessionChecked) {
    return (
      <div className="page-content pw-loading" role="status" aria-live="polite">
        <div className="pw-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!isAdminUser(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const App = () => {
  const [user, setUser] = useState(readStoredUser);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [latestNotifications, setLatestNotifications] = useState([]);
  
  // Create a ref for the notification dropdown container
  const notificationDropdownRef = useRef(null);

  const { mode: themeMode } = useTheme();
  const isDarkMode = themeMode === 'dark';

  // Re-sync theme attributes on mount (index.html applies them pre-paint)
  useEffect(() => {
    applyStoredTheme();
  }, []);

  useEffect(() => {
    if (!user) {
      // Initial user=null is not logout — keep last-saved premarket/afterhours.
      clearLiveMarketClientCache();
      return undefined;
    }
    const stopLiveMarketCache = startLiveMarketClientCache();
    return () => {
      stopLiveMarketCache();
      // Keep last premarket/afterhours snapshot across remounts; logout clears extended.
      clearLiveMarketClientCache();
    };
  }, [user]);

  // Function to fetch latest notifications
  const fetchLatestNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await authFetch(`/api/notifications/${user.email}/latest?limit=5`);
      if (response.ok) {
        const data = await response.json();
        setLatestNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error("Error fetching latest notifications:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      fetchLatestNotifications();
    } else {
      setLatestNotifications([]);
    }
  }, [user, fetchLatestNotifications]);

  // Function to mark notifications as read
  const markNotificationsAsRead = useCallback(() => {
    setUnreadNotifications(false);
  }, []);

  // Listen for notification events from Messages component
  useEffect(() => {
    const handleNewNotification = (event) => {
      setUnreadNotifications(true);
      fetchLatestNotifications();
    };
    
    window.addEventListener('new-notification', handleNewNotification);
    
    return () => {
      window.removeEventListener('new-notification', handleNewNotification);
    };
  }, [fetchLatestNotifications]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationDropdownRef.current && 
        !notificationDropdownRef.current.contains(event.target) &&
        notificationDropdownOpen
      ) {
        setNotificationDropdownOpen(false);
      }
    };
    
    // Add event listener when dropdown is open
    if (notificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationDropdownOpen]);

  // Toggle notification dropdown
  const toggleNotificationDropdown = () => {
    if (!notificationDropdownOpen) {
      fetchLatestNotifications();
    }
    setNotificationDropdownOpen(!notificationDropdownOpen);
    if (unreadNotifications) {
      markNotificationsAsRead();
    }
  };

  useEffect(() => {
    let stopRefresh = () => {};

    const restoreSession = async () => {
      try {
        const storedUserRaw = localStorage.getItem('user');
        if (!storedUserRaw) {
          setUser(null);
          return;
        }

        const sessionOk = await bootstrapAuthSession();
        if (!sessionOk) {
          clearAuthSession();
          setUser(null);
          return;
        }

        const storedUser = JSON.parse(localStorage.getItem('user') || storedUserRaw);
        setUser(storedUser);
        stopRefresh = startAuthSessionRefresh();
        if (storedUser?.email) {
          hydrateFilterPresets(storedUser.email).catch((error) => {
            console.warn('Error hydrating filter presets:', error);
          });
        }
      } finally {
        setSessionChecked(true);
      }
    };

    restoreSession();

    return () => {
      stopRefresh();
    };
  }, []);

  return (
    <Router>
      <div className="app">
        <div className="app-sticky-top">
        <header>
          <div className="header-inner">
            <div className="header-content">
              <h1>
                <Link to="/" className="header-logo-link">
                  <BidsigLogo size={36} color="#ffffff" className="bidsig-logo-svg header-logo-svg" aria-hidden="true" />
                  <span className="header-logo-wordmark">Bidsig</span>
                </Link>
              </h1>
              <div className="header-actions">
                <button
                  type="button"
                  className={`header-theme-switch ${isDarkMode ? 'is-dark' : ''}`}
                  onClick={toggleThemeMode}
                  role="switch"
                  aria-checked={isDarkMode}
                  aria-label="Toggle dark mode"
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  <span className="header-theme-switch-knob" aria-hidden="true" />
                  <Sun className="header-theme-switch-icon header-theme-switch-icon--sun" size={14} aria-hidden="true" />
                  <Moon className="header-theme-switch-icon header-theme-switch-icon--moon" size={14} aria-hidden="true" />
                </button>
                <div className="notification-bell-container" ref={notificationDropdownRef}>
                  <div 
                    className={`notification-bell ${unreadNotifications ? 'has-notifications' : ''}`}
                    onClick={toggleNotificationDropdown}
                  >
                    <Bell className="icon" />
                    {unreadNotifications && <span className="notification-indicator"></span>}
                  </div>
                  
                  {notificationDropdownOpen && (
                    <div className="notification-dropdown">
                      <div className="notification-dropdown-header">
                        <h3>Recent Alerts</h3>
                        <Link to="/messages" onClick={() => setNotificationDropdownOpen(false)}>
                          View All
                        </Link>
                      </div>
                      <div className="notification-dropdown-content">
                        {latestNotifications.length > 0 ? (
                          latestNotifications.map(notification => {
                            const isStockAlert = notification.type === 'stock_alert';
                            const symbolId = notification.symbol_id || notification.symbol || '';
                            const linkTo = isStockAlert && symbolId
                              ? `/stock/${encodeURIComponent(String(symbolId).toUpperCase())}`
                              : '/messages';
                            return (
                            <Link 
                              key={notification.note_id} 
                              to={linkTo} 
                              className="notification-item-preview"
                              onClick={() => setNotificationDropdownOpen(false)}
                            >
                              <div className="notification-preview-header">
                                <span className="notification-symbol">{notification.symbol}</span>
                                <span className="notification-preview-type">
                                  {isStockAlert ? 'Stock' : 'News'}
                                </span>
                                <span className="notification-time">
                                  {new Date(notification.insert_date).toLocaleTimeString()}
                                </span>
                              </div>
                              <p className="notification-preview-message">{notification.message}</p>
                            </Link>
                            );
                          })
                        ) : (
                          <p className="no-notifications-message">No recent alerts</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>
        <Navbar user={user} setUser={setUser} />
        </div>
        <div className="content-wrapper">
          <Routes>
            <Route path="/" element={<HomePage user={user} />} />
            <Route path="/screener" element={<BidsigDashboard />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/movers" element={<Movers />} />
            <Route path="/news" element={<News />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/about" element={<About />} />
            <Route path="/support" element={<Support />} />
            <Route path="/learn" element={<Learn />} />
            <Route path="/blog" element={<Navigate to="/learn" replace />} />
            <Route path="/contact" element={<Navigate to="/support" replace />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/stock/:symbolId" element={<StockDetail />} />
            <Route path="/newsletter" element={<Newsletter />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/watchlist" element={<Watchlist />} />
            <Route path="/profile" element={<Profile />} />
            <Route
              path="/admin"
              element={
                <AdminRoute user={user} sessionChecked={sessionChecked}>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/cache-viewer"
              element={
                <AdminRoute user={user} sessionChecked={sessionChecked}>
                  <CacheViewer />
                </AdminRoute>
              }
            />
            {/* Add dummy routes for new pages */}
            <Route path="/careers" element={<Careers />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
          </Routes>
        </div>
        <div className="app-global-disclaimer">
          <Disclaimer variant="banner">
            <strong>Informational purposes only — not investment advice.</strong>{' '}
            Bidsig is a research tool and is not a registered investment adviser or
            broker-dealer. Signals, AI overviews, alerts, and backtests are general
            information applied uniformly, are not personalized recommendations,
            solicitations, or endorsements of any security, and do not create any
            advisory relationship. Past or backtested performance does not guarantee
            future results. Consult a licensed professional before investing.
          </Disclaimer>
        </div>
        <NewsletterSignupBanner />
        <Footer />
      </div>
    </Router>
  );
};

// Dummy component for pages that don't exist yet
const DummyPage = ({ title }) => (
  <div className="page-content">
    <h2>{title}</h2>
    <p>This page is under construction. Please check back later!</p>
  </div>
);

export default App;